import axios from 'axios';
import { noteMutationStart, noteMutationEnd } from './reloadGuard'; // [AUTO-REFRESH 2026-08-03]
import { noteLeadApiFailure, noteLeadApiOk } from './leadClientErrors'; // [LEAD-ERROR-LOG 2026-10-30]
import { Payment, PaymentsResponse, FiltersResponse, InventoryResponse, InventoryFiltersResponse, InventoryRow, RefundsResponse, RefundFiltersResponse, DisputesResponse, DisputeFiltersResponse, NewSalesResponse, NewSalesSummary, NewSalesFiltersResponse } from '../types';

// Origin of the API server for top-level navigations (the Google sign-in
// redirect). CRA's dev proxy does NOT forward document navigations, so the
// login link must point at the server origin directly. In production the app
// and API share an origin, so this resolves to empty (same-origin) by default.
export const AUTH_BASE = process.env.REACT_APP_AUTH_BASE ?? 'http://localhost:4000';

const api = axios.create({
  baseURL: '/api',
  withCredentials: true, // send the session cookie on every request
});

// ── Instant tab-switching cache (stale-while-revalidate) ─────────────────────
// GET responses are kept in memory and served instantly — no spinner — when a
// section is revisited, while a background request silently refreshes the entry
// for next time. Any mutation (non-GET) clears the cache so the next read is
// fresh. Net effect: switching tabs feels instant without serving stale data
// after edits. Auth calls are never cached so session state stays live.
const GET_CACHE = new Map<string, { time: number; response: any }>();
const CACHE_TTL = 5 * 60 * 1000; // serve a cached page instantly for up to 5 min
// Don't fire a background revalidation for entries fresher than this. Rapid
// tab-switching (or a page that re-reads on a quick state change) otherwise
// hammers the remote DB with redundant round-trips and re-parses large JSON
// payloads on the main thread — pure waste, since the data can't have changed:
// every write clears the whole cache below, so the only thing that mutates data
// between reads is a server-side background job, which 30s freshness still
// surfaces imperceptibly fast on the next visit.
const REVALIDATE_AFTER = 30 * 1000;
// [TAB-INSTANT 2026-08-20] Datasets watched by the realtime data-version poller
// (services/realtime.ts DATASET_PREFIXES — keep the two lists in step). For these, an
// entry that is OLDER than CACHE_TTL but still in the cache is not stale at all: any
// real change would have deleted it within one poll (~4s) via
// invalidateGetCacheByPrefix, and every successful write clears the whole cache
// anyway. So a surviving entry is served instantly at ANY age (bounded below) instead
// of making the user watch a skeleton while the network re-downloads identical bytes.
// Untracked endpoints (my-sales, sales-scoreboard, dialing-report, stats, filters,
// users…) keep the original 5-minute limit — nothing signals when THEY change.
const VERSION_TRACKED_PREFIXES = [
  '/payments', '/inventory', '/refunds', '/disputes', '/eom', '/eom-table',
  '/delayed-payments', '/retention-pipeline', '/team-performance', '/new-sales',
  '/process-analysis', '/pm-recommendations', // [PM-RECS 2026-08-25]
  '/sales-pipeline', // [SALES-PIPELINE 2026-10-07]
];
// Backstop for the tracked serve: if the poller itself is down (old server, network),
// a very old entry must not render for ever. Far past any real attention gap.
const TRACKED_SERVE_MAX_MS = 2 * 60 * 60 * 1000;
const isVersionTracked = (url: string) =>
  VERSION_TRACKED_PREFIXES.some((p) => url === p || url.startsWith(p + '/') || url.startsWith(p + '?'));

// ── [SKELETON-DWELL 2026-08-20] The deliberate ~1s skeleton on every tab open ────────
// The user's explicit choice (2026-08-20): every tab open shows its skeleton for about a
// second, THEN the data — a uniform, professional transition — even when the data was
// ready instantly. Enforced HERE, in the data layer, so no page needs edits: any
// cacheable GET that starts inside the window after a navigation resolves no sooner
// than the window's end. Pages already render their skeleton while their first fetch is
// pending, so holding the resolve holds the skeleton; slower fetches are never delayed
// further (the hold only tops the wait UP TO ~1s, it never adds to a long fetch).
// peekCache cooperates below by not seeding inside the window, or seeded pages would
// skip their skeleton entirely. In-page interactions (filter/month changes, refetches
// after the window) are untouched — this is a tab-OPEN transition only.
// [TAB-HOLD 2026-10-12, user] "only for the tab change … 1 second hold" (earlier that day "i dont want the hold" had set
// this to 0): 1000 again, for a REAL tab change only — a push or Back/Forward that lands on a DIFFERENT page (its
// pathname, /disputes folded into /refunds: AppLayout's page key). The Refunds/Disputes pill, pressing the dock tab you
// are on, a query-only push and replaceState open no window. And nothing the user DOES waits: the first press or key
// after a tab change ends the window at once (endDwell below).
const NAV_DWELL_MS = 1000;
let lastNavAt = Date.now(); // the initial page load counts as a navigation
// [TAB-HOLD 2026-10-12, user] Each window's early exit: what a window holds also lets go when its gate opens.
const newDwellGate = () => { let letGo = () => {}; const open = new Promise<void>((r) => { letGo = () => r(); }); return { open, letGo }; };
let dwellGate = newDwellGate();
if (typeof window !== 'undefined' && window.history) {
  const stamp = () => { lastNavAt = Date.now(); dwellGate = newDwellGate(); };
  // [TAB-HOLD 2026-10-12, user] The page a URL shows — the key AppLayout gives PageFade (the pathname, /disputes →
  // /refunds) — and the page on screen now, kept for popstate (by then the URL has already changed).
  const pageOf = (url?: string | URL | null): string => {
    try {
      return new URL(url == null ? window.location.href : String(url), window.location.href).pathname.replace(/^\/disputes(?=\/|$)/, '/refunds');
    } catch { return ''; }
  };
  let shownPage = pageOf();
  // React-router drives tab switches through history.pushState; stamping it here runs
  // BEFORE the new page's first render (unlike a layout effect), which peekCache needs.
  // replaceState is deliberately NOT stamped: redirects shouldn't restart the window.
  const origPush = window.history.pushState.bind(window.history);
  const origReplace = window.history.replaceState.bind(window.history);
  window.history.pushState = function (...args: any[]) {
    const to = pageOf(args[2]);
    if (to !== pageOf()) stamp(); // [TAB-HOLD] the same page again is no tab change
    shownPage = to;
    return (origPush as any)(...args);
  } as any;
  // [TAB-HOLD] replaceState only keeps shownPage true (a redirect); it still never stamps.
  window.history.replaceState = function (...args: any[]) { shownPage = pageOf(args[2]); return (origReplace as any)(...args); } as any;
  window.addEventListener('popstate', () => { // browser back/forward is a tab switch too — [TAB-HOLD] onto a different page
    const to = pageOf();
    if (to !== shownPage) stamp();
    shownPage = to;
  });
  // [TAB-HOLD 2026-10-12, user] The first press or key after a tab change ends its window at once, so whatever it opens
  // (a panel, a menu, a filter) loads unheld. Capture on window = before any page handler; and the dock press or Enter
  // that switches tabs comes BEFORE the switch (BottomNav navigates a frame later), so it never ends the window its own
  // switch opens — and its release (pointerup / click / keyup) never ends a window at all.
  // What the window already holds lets go when that press or key is RELEASED, two frames on — unless it switched tabs
  // by then (the dock a frame after its press; a link or a tap on its click): that data belongs to the page being left,
  // so it keeps its time instead of landing on that page on its way out (a flash, and a render for nothing).
  let letGoOnRelease: (() => void) | null = null;
  const endDwell = (e: Event) => {
    if (!e.isTrusted || (e as KeyboardEvent).repeat || Date.now() >= navDwellDeadline()) return;
    lastNavAt = Date.now() - NAV_DWELL_MS; // the window ends now: nothing started from here on is held
    const ended = lastNavAt;
    const gate = dwellGate;
    letGoOnRelease = () => requestAnimationFrame(() => requestAnimationFrame(() => { if (lastNavAt === ended) gate.letGo(); }));
  };
  const onRelease = (e: Event) => {
    const letGo = letGoOnRelease;
    if (!e.isTrusted || !letGo) return;
    letGoOnRelease = null;
    letGo();
  };
  window.addEventListener('pointerdown', endDwell, true);
  window.addEventListener('keydown', endDwell, true);
  ['pointerup', 'pointercancel', 'keyup'].forEach((type) => window.addEventListener(type, onRelease, true));
}
const navDwellDeadline = () => lastNavAt + NAV_DWELL_MS;

const inFlight = new Set<string>();
const baseAdapter: any = axios.getAdapter(api.defaults.adapter ?? axios.defaults.adapter);

// ── Live data-refresh bus ───────────────────────────────────────────────────
// Every successful write (any non-GET) notifies subscribers so they can silently
// refetch. This is what keeps derived views — In Bank summary, Inventory KPI
// strip, footer totals, etc. — in lockstep with their table WITHOUT a full page
// refresh. A view subscribes via `onDataChange` and re-runs its own silent load;
// because the bus fires for ANY write, a newly-added summary just needs to
// subscribe once and it can never silently go stale again.
//
// Notifications are coalesced (one tick) so a burst of writes in a single action
// triggers a single refetch, and the cache is already cleared by the time the
// notification fires so every refetch hits fresh data.
type DataChangeListener = () => void;
const dataChangeListeners = new Set<DataChangeListener>();
let dataChangeTimer: ReturnType<typeof setTimeout> | null = null;

/** Subscribe to "some write just succeeded". Returns an unsubscribe fn. */
export function onDataChange(listener: DataChangeListener): () => void {
  dataChangeListeners.add(listener);
  return () => { dataChangeListeners.delete(listener); };
}

function scheduleDataChange(): void {
  if (dataChangeTimer) return;
  dataChangeTimer = setTimeout(() => {
    dataChangeTimer = null;
    // A throwing listener must never break the bus for the others.
    dataChangeListeners.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
  }, 120);
}

// [REALTIME] 2026-06-23 — support for the real-time data-version poller.
// =============================================================================
// Drop the cached GET entries whose URL contains any of these prefixes, so the
// next read for a changed dataset hits the server instead of a stale cache.
// (A write clears the WHOLE cache; this is the targeted equivalent for a
// background DB change detected via /data-version.)
export function invalidateGetCacheByPrefix(prefixes: string[]): void {
  if (!prefixes.length) return;
  for (const key of Array.from(GET_CACHE.keys())) {
    if (prefixes.some((p) => key.includes(p))) GET_CACHE.delete(key);
  }
}

export interface DataVersions {
  versions: Record<string, number>;
  lastChangeAt: number | null;
  startedAt: number | null;
}

// The tiny change-notification poll. Excluded from the GET cache (see isCacheable)
// so it always reflects the latest server-side counters.
export async function fetchDataVersions(): Promise<DataVersions> {
  const { data } = await api.get<DataVersions>('/data-version');
  return data;
}

// Order-independent key that ignores `undefined` params, so the same logical
// request (e.g. {month:'2026-06'} vs {account:undefined, month:'2026-06'}) maps
// to one cache entry — this is what lets a prefetch reliably hit the page's call.
const cacheKey = (config: any) => {
  const p = config.params || {};
  const norm = Object.keys(p)
    // [TAB-FAST 2026-06-26] Drop "absent" filters so the SAME logical request maps to
    // ONE entry — undefined/null, empty string ('' = no account/search filter) AND empty
    // array ([] = no multi-select filter, e.g. Inventory's type/pm/brand/status). A page's
    // first load sends those empties; the idle prefetch omits them. Treating them as absent
    // is what lets the prefetch (and a page revisit) reliably hit the same cache entry.
    // NOTE: 0 / false are real values and are intentionally kept (e.g. pageSize=0 = "all rows").
    .filter((k) => {
      const v = p[k];
      if (v === undefined || v === null || v === '') return false;
      if (Array.isArray(v) && v.length === 0) return false;
      return true;
    })
    .sort()
    .map((k) => `${k}=${p[k]}`)
    .join('&');
  return `${config.baseURL || ''}${config.url}|${norm}`;
};

// [TAB-FAST 2026-06-26] Synchronous peek into the SWR GET cache. Returns the cached
// payload if it's present and still fresh (within CACHE_TTL), else undefined. Lets a page
// SEED its first render from cache (no skeleton flash on a warmed/revisited tab) while its
// normal load still runs and revalidates. Read-only; never triggers a request.
export function peekCache<T = any>(url: string, params?: Record<string, any>): T | undefined {
  // [SKELETON-DWELL 2026-08-20] No seeding inside the tab-open window: a seed would paint
  // the data immediately and skip the page's skeleton, and the user explicitly wants the
  // ~1s skeleton on every tab open. The page falls back to its skeleton + normal load,
  // and the adapter serves that load from this same cache the moment the window ends.
  if (Date.now() < navDwellDeadline()) return undefined;
  const entry = GET_CACHE.get(cacheKey({ baseURL: '/api', url, params }));
  if (!entry) return undefined;
  // [TAB-INSTANT 2026-08-20] Same serve rule as the adapter below: fresh always, and any
  // age (bounded) for version-tracked datasets — a surviving entry means "unchanged".
  const age = Date.now() - entry.time;
  if (age < CACHE_TTL || (age < TRACKED_SERVE_MAX_MS && isVersionTracked(url))) {
    return entry.response.data as T;
  }
  return undefined;
}

const isCacheable = (config: any) => {
  const method = (config.method || 'get').toLowerCase();
  const url: string = config.url || '';
  // [REALTIME] 2026-06-23 — the version poll must never be served from the cache,
  // or it could report a stale version and miss a change.
  // [LIVE-CONNECTIONS 2026-09-30] Same for the App Alerts live connections card: a cached answer
  // would show the previous reading on every 5-second refresh.
  // [CARD-ACTIVITY / SUGGEST 2026-10-07] The Sales Pipeline's activity counts and typing suggestions are always asked
  // fresh: a cached answer would show old counts (HubSpot/Google Meet change without any dashboard write).
  // [MAIL-CONTENT 2026-10-13] …and a Lead Assignment email (with its pictures it can weigh a few MB — not kept in memory).
  // [LEAD-JOBS 2026-10-14] …and the progress of an Enrich Data / Assign to HubSpot run (asked every 2 s, always fresh).
  // [ASSIGNEE-NAMES 2026-10-16] …and Assignee Names (the live HubSpot Sales people + who is Active — never an old answer).
  return method === 'get' && !url.includes('/auth/') && !url.includes('/data-version')
    && !url.includes('/app-alerts/db-connections') && !url.includes('/app-alerts/lead-assignment') // [LEAD-LIVE-CARD 2026-10-21] live readings
    && !url.includes('/sales-pipeline/activity') && !url.includes('/sales-pipeline/suggest')
    && !/\/lead-assignment\/\d+\/email/.test(url) && !url.includes('/lead-assignment/job')
    && !url.includes('/lead-assignment/assignee-names')
    && !url.includes('/my-sales/report'); // [MYSALES-REPORT 2026-10-28] a downloaded file is made from what is true at the click
};

/* [LOAD-STATES 2026-09-29, user] Page loads (cacheable GETs only — never a write, never the
   version poll) get two things they never had:
     • a time limit. axios' default is none, so a request the server never answered kept the tab's
       skeleton up for ever (measured: still loading after 45 s). Now it gives up and the page shows
       its "Couldn't load … Trying again in 5 s" card (components/LoadState).
     • two quiet retries for a PASSING failure — no response at all (a dropped connection) or a
       502/503/504 from the proxy — 1 s and 3 s apart, before the page ever hears about it. A real
       answer (401, 403, 404, 500) or a time-out is never retried here; the page's own card handles those.
   A GET that sets its own `timeout` keeps it. */
const GET_TIMEOUT_MS = 45_000;
const GET_RETRY_DELAYS_MS = [1000, 3000];
const isPassingFailure = (err: any): boolean => {
  if (axios.isCancel(err) || err?.code === 'ERR_CANCELED') return false;
  // A request that already waited the full time limit is not retried here: two more 45 s waits
  // would keep the skeleton up for over two minutes. The page's own countdown card takes over.
  if (err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT') return false;
  const status = Number(err?.response?.status) || 0;
  return status ? status === 502 || status === 503 || status === 504 : true;
};
/* [DB-OUTAGE-FIX 2026-10-28, user] The server marks a HALF-measured answer with X-Data-Partial (server
   middleware/responseCache markPartial: a part of it could not be read and is sent as "unknown" / "—").
   It is shown, but never kept here: a revisit must ask again instead of seeding the tab from it, and a
   background revalidate keeps the older, complete answer rather than overwriting it. */
const isPartialAnswer = (resp: any): boolean => {
  const h = resp?.headers;
  if (!h) return false;
  return !!(typeof h.get === 'function' ? h.get('x-data-partial') : h['x-data-partial']);
};
async function getWithRetry(config: any): Promise<any> {
  const cfg = config.timeout ? config : { ...config, timeout: GET_TIMEOUT_MS };
  for (let i = 0; ; i++) {
    try {
      return await baseAdapter(cfg);
    } catch (err) {
      if (i >= GET_RETRY_DELAYS_MS.length || !isPassingFailure(err)) throw err;
      await new Promise((r) => setTimeout(r, GET_RETRY_DELAYS_MS[i]));
    }
  }
}

api.defaults.adapter = (async (config: any) => {
  const method = (config.method || 'get').toLowerCase();

  // Any write invalidates the read cache so subsequent reads are fresh.
  if (method !== 'get') {
    GET_CACHE.clear();
    const resp = await baseAdapter(config);
    // Tell every subscribed view to refetch — but only after a successful write,
    // since a failed one changed nothing. The cache is already cleared above, so
    // their refetch sees the just-committed state.
    const status = Number(resp?.status) || 0;
    if (status >= 200 && status < 300) scheduleDataChange();
    return resp;
  }

  if (!isCacheable(config)) return baseAdapter(config);

  const key = cacheKey(config);
  const entry = GET_CACHE.get(key);
  const now = Date.now();

  // [SKELETON-DWELL 2026-08-20] A request that starts inside the tab-open window resolves
  // no sooner than the window's end — this is what keeps the just-opened page's skeleton
  // on screen for the deliberate ~1s. Requests that start after the window (in-page
  // interactions, background refetches, polls) get a deadline of 0 and are untouched,
  // and a fetch already slower than the window is never delayed further.
  const dwellDeadline = now < navDwellDeadline() ? navDwellDeadline() : 0;
  const dwellExit = dwellGate.open; // [TAB-HOLD 2026-10-12, user] …or sooner, when this window's gate opens (endDwell)
  const dwellHold = async () => {
    const left = dwellDeadline - Date.now();
    if (left > 0) await Promise.race([new Promise((r) => setTimeout(r, left)), dwellExit]);
  };

  // [CACHE-REVALIDATE-PARSE 2026-09-23, user] baseAdapter is axios' LOW-LEVEL adapter: it returns
  // the body as raw JSON TEXT, and only axios' own pipeline (dispatchRequest -> transformResponse)
  // turns it into an object afterwards. A normal load goes through that pipeline, and it parses the
  // very response object stored in GET_CACHE. This background call does NOT, so it used to store the
  // raw text, and peekCache() then handed pages a STRING instead of their payload. StatsPage crashed
  // on it with "rows is not iterable"; 10 pages seed from peekCache. Parse it here the same way. If
  // the body is not JSON, keep the existing good entry instead of overwriting it with text.
  const revalidate = () => {
    if (inFlight.has(key)) return;
    inFlight.add(key);
    baseAdapter({ ...config })
      .then((resp: any) => {
        if (typeof resp?.data === 'string') {
          try { resp.data = JSON.parse(resp.data); } catch { return; }
        }
        if (isPartialAnswer(resp)) return; // [DB-OUTAGE-FIX 2026-10-28] keep the complete entry
        GET_CACHE.set(key, { time: Date.now(), response: resp });
      })
      .catch(() => {})
      .finally(() => inFlight.delete(key));
  };

  if (entry) {
    const age = now - entry.time;
    // [TAB-INSTANT 2026-08-20] Fresh entries serve exactly as before. A version-tracked
    // entry PAST CACHE_TTL also serves instantly — its continued existence proves the
    // data hasn't changed (see VERSION_TRACKED_PREFIXES above) — while the background
    // revalidate below re-times it. Untracked datasets past CACHE_TTL still fall
    // through to a blocking network fetch, the pre-change behaviour.
    if (age < CACHE_TTL || (age < TRACKED_SERVE_MAX_MS && isVersionTracked(String(config.url || '')))) {
      // Refresh in the background for the next visit — but skip it for very-fresh
      // entries so a burst of revisits doesn't fire redundant remote round-trips.
      if (age >= REVALIDATE_AFTER) revalidate();
      await dwellHold(); // [SKELETON-DWELL] instant data still waits out the ~1s skeleton
      return { ...entry.response, config, cached: true };
    }
  }

  const resp = await getWithRetry(config); // [LOAD-STATES 2026-09-29]
  // cache first, hold only the return — [DB-OUTAGE-FIX 2026-10-28] never a half-measured answer
  if (!isPartialAnswer(resp)) GET_CACHE.set(key, { time: Date.now(), response: resp });
  await dwellHold();
  return resp;
}) as any;

// When any API call comes back 401, the session is gone/expired — let the app
// drop back to the login screen. AuthContext registers the handler.
let unauthorizedHandler: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  unauthorizedHandler = fn;
}

// [AUTO-REFRESH 2026-08-03] Count mutating requests so the update banner never reloads the
// tab mid-write. Reloading during a PUT/POST aborts it browser-side while the SERVER may
// still commit it — the user would have no way to know whether their change landed. One
// pair of interceptors covers every write in the app; no page has to opt in.
//
// The flag on `config` is what pairs the decrement to the increment: it guarantees we only
// ever decrement for requests we actually counted, even if another interceptor rewrites the
// method or a request is rejected before it is sent.
const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);
type CountedConfig = { __countedMutation?: boolean; method?: string };

api.interceptors.request.use(
  (config) => {
    if (MUTATING_METHODS.has(String(config.method || '').toLowerCase())) {
      (config as CountedConfig).__countedMutation = true;
      noteMutationStart();
    }
    return config;
  },
  (error) => {
    // The request never left, so nothing was counted for it.
    return Promise.reject(error);
  }
);

/** Balance the counter for a settled request, exactly once. */
function releaseCountedMutation(config: any): void {
  if (config && (config as CountedConfig).__countedMutation) {
    (config as CountedConfig).__countedMutation = false;
    noteMutationEnd();
  }
}

api.interceptors.response.use(
  (resp) => { releaseCountedMutation(resp?.config); noteLeadApiOk(resp?.config); return resp; },
  (error) => {
    releaseCountedMutation(error?.config);
    noteLeadApiFailure(error); // [LEAD-ERROR-LOG 2026-10-30] Lead Management only: App Alerts hears of a request that got no answer
    const status = error?.response?.status;
    const url: string = error?.config?.url || '';
    // Ignore 401s from the auth probe itself — that's the normal "not logged in"
    // signal handled by AuthContext, not a session that just expired.
    // [PM-LOGIN 2026-08-11] /auth/login is exempt for the same reason: a wrong password is a
    // 401 by design. Without this, typing the password wrong would fire the global
    // "you were signed out" handler and the login form would be torn down mid-typo.
    if (status === 401 && !url.includes('/auth/me') && !url.includes('/auth/login')) {
      // [ACCESS-KILLSWITCH 2026-07-07] The server tags a 401 caused by an access change
      // (session_epoch bump / deactivation) as `access_changed`. Flag it so the LoginPage
      // can explain WHY they were signed out ("your access was updated") instead of a bare
      // login screen. sessionStorage survives the drop-to-login re-render; LoginPage clears it.
      if (error?.response?.data?.error === 'access_changed') {
        try { sessionStorage.setItem('dt_access_notice', '1'); } catch { /* ignore */ }
      }
      unauthorizedHandler?.();
    }
    return Promise.reject(error);
  }
);

// ── Auth ──

export interface MeResponse {
  id: number;
  name: string | null;
  email: string;
  picture: string | null;
  role: 'admin' | 'lead' | 'member';
  is_admin: boolean;
  /** HubSpot PM/Sales classification. Drives the Dialing Report's department
   *  gating: 'pm' → PM side only, 'sales' → Sales side only, 'both'/null (and
   *  admins/seniors) → both sides with the PM/Sales toggle. */
  directory_role: 'pm' | 'sales' | 'both' | 'leads' | null; // [LEADS-ROLE]
  allowed_sections: string[];
  /** [TL-REPORT-ONLY 2026-10-19] one of the four (server services/leadReportOnly.ts) whose Lead Management is the Lead Generator Report only */
  lead_report_only?: boolean;
  pm_name: string | null;
  team_id: number | null;
  status: 'active' | 'pending' | 'deactivated';
  /** Performance tabs this user may view: e.g. ['shreyansh'] scopes them to that
   *  one team's tab (owner or member alike). Empty = unrestricted (admins/seniors
   *  see every tab via `is_admin`/all-scope; non-PM grantees fall back to all tabs). */
  performance_teams: string[];
  /** True only for actual team leads — they auto-get the Performance section.
   *  Regular members are scoped but must be granted the section explicitly. */
  performance_owner: boolean;
  /** [ALL-PM-SENIOR 2026-06-23] Senior allow-list (e.g. Sana/Amaan): My Inventory
   *  defaults the PM dropdown to "All PMs" (with the "All PMs" option visible)
   *  instead of their own name. False for everyone else, incl. other seniors. */
  inventory_all_pms: boolean;
  /** [MYINV-PM-ACCESS 2026-06-26] Senior flag. Admins + seniors may edit the PM column
   *  in My Inventory (on top of Status / Discount / At-Risk Expected Payment). */
  is_senior: boolean;
  /** [PM-LOGIN 2026-08-11] True when this session came from the username/password form rather
   *  than Google. Drives the stripped-back header (brand + profile only). */
  is_password_account?: boolean;
  /** [ROW-BUTTON 2026-09-30] May open the Team ROW Sales board (admins, Team ROW - Sales and Vishal Suryavanshi
   *  since [ROW-ACCESS-TEAM 2026-10-01] — decided on the server). Shows the header's "ROW Dashboard" button + the /sales-row route. */
  can_sales_row?: boolean;
}

/** [DB-OUTAGE 2026-11-03] Said when the server or the database gives no real answer (never a sign-out). */
export const SERVER_NOT_ANSWERING = 'The server is not answering right now. Please try again in a moment.';

export async function fetchMe(): Promise<MeResponse> {
  // [DB-OUTAGE 2026-11-03] A server that hangs counts as "not answering" after 15 s, so AuthContext can ask again.
  const { data } = await api.get<MeResponse>('/auth/me', { timeout: 15000 });
  return data;
}

export async function logout(): Promise<void> {
  await api.post('/auth/logout');
}

/**
 * [PM-LOGIN 2026-08-11] Username/password sign-in. On success the server sets the same session
 * cookie the Google flow does, so the caller only has to refresh() the auth context.
 *
 * Throws a plain Error carrying a human-readable message — the login screen shows it verbatim,
 * and it deliberately never distinguishes a wrong username from a wrong password.
 */
export async function loginWithPassword(username: string, password: string): Promise<void> {
  try {
    await api.post('/auth/login', { username, password });
  } catch (err: any) {
    const status = err?.response?.status;
    const code = err?.response?.data?.error;
    if (status === 429) {
      const secs = Number(err?.response?.data?.retry_after_s) || 900;
      throw new Error(`Too many failed attempts. Try again in ${Math.ceil(secs / 60)} minutes.`);
    }
    if (status === 401 || code === 'invalid_credentials') {
      throw new Error('Incorrect username or password.');
    }
    if (code === 'missing_credentials') {
      throw new Error('Enter both a username and a password.');
    }
    // [DB-OUTAGE 2026-11-03] No answer, or the server / database failed: not the person's fault, so say what it is.
    if (!status || status >= 500) {
      throw new Error(SERVER_NOT_ANSWERING);
    }
    throw new Error('Sign-in failed. Please try again.');
  }
}

// ── User Management (admin) ──

export interface DirectoryUser {
  id: number;
  name: string | null;
  email: string;
  picture: string | null;
  directory_role: 'pm' | 'sales' | 'both' | 'leads' | null; // [LEADS-ROLE]
  is_admin: boolean;
  is_senior: boolean;            // sees all teams' Performance + every PM in My Inventory
  allowed_sections: string[];
  pm_name: string | null;
  pm_team: string | null;        // HubSpot PM team name, e.g. "Team Ilma - PM"
  sales_team: string | null;     // HubSpot Sales team name, e.g. "Team Aman - Sales"
  visible_to_owner: boolean;     // can this person's PM (team owner) see them
  status: 'active' | 'pending' | 'deactivated';
  last_login_at: string | null;
}

export interface UpdateDirectoryUserInput {
  is_admin?: boolean;
  is_senior?: boolean;
  allowed_sections?: string[];
  status?: 'active' | 'pending' | 'deactivated';
  pm_name?: string | null;
  visible_to_owner?: boolean;
}

export async function fetchUsers(): Promise<{ users: DirectoryUser[]; sections: string[] }> {
  const { data } = await api.get<{ users: DirectoryUser[]; sections: string[] }>('/users');
  return data;
}

export async function updateDirectoryUser(id: number, patch: UpdateDirectoryUserInput): Promise<DirectoryUser> {
  const { data } = await api.put<DirectoryUser>(`/users/${id}`, patch);
  return data;
}

export async function createDirectoryUser(
  email: string,
  name: string,
  directory_role: 'pm' | 'sales' | 'leads' | null = null,
): Promise<DirectoryUser> {
  const { data } = await api.post<DirectoryUser>('/users', { email, name, directory_role });
  return data;
}

export async function deleteDirectoryUser(id: number): Promise<void> {
  await api.delete(`/users/${id}`);
}

// ── Application Logs (audit trail — admin) ──

export interface ActivityLog {
  id: number;
  user_id: number | null;
  user_email: string | null;
  user_name: string | null;
  section: string;
  action: string;
  method: string;
  path: string;
  target_id: string | null;
  status_code: number | null;
  success: boolean;
  details: Record<string, any> | null;
  ip: string | null;
  user_agent: string | null;
  created_at: string | null;
}

export interface FetchActivityLogsParams {
  page?: number;
  pageSize?: number;
  section?: string;
  search?: string;
  success?: 'all' | 'ok' | 'fail';
  userEmail?: string;
  from?: string;
  to?: string;
}

export interface ActivityLogsResponse {
  logs: ActivityLog[];
  pagination: { page: number; pageSize: number; totalRows: number; totalPages: number };
  sections: string[];
  users: { email: string; name: string | null }[];
}

export async function fetchActivityLogs(params: FetchActivityLogsParams = {}): Promise<ActivityLogsResponse> {
  const { data } = await api.get<ActivityLogsResponse>('/activity-logs', { params });
  return data;
}

// Permanently delete a single audit-log row (admin). HARD delete — not a trash move.
export async function deleteActivityLog(id: number): Promise<void> {
  await api.delete(`/activity-logs/${id}`);
}

/**
 * [MULTI-MONTH 2026-07-15] Flatten a `months: string[]` param into `months=a,b,c`.
 * axios would otherwise emit `months[]=a&months[]=b`, which the server's
 * parseMonthsParam does not read. An empty/absent array drops the param entirely,
 * which the server treats as "no month filter". Every month-filtered fetch below
 * goes through this so the wire format can never drift between endpoints.
 */
export function monthsParams<T extends { months?: string[] }>(params: T): Record<string, unknown> {
  const { months, ...rest } = params as T & { months?: string[] };
  const q: Record<string, unknown> = { ...rest };
  if (months && months.length) q.months = months.join(',');
  return q;
}

interface FetchPaymentsParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  account?: string;
  month?: string;
  /** [MULTI-MONTH 2026-07-15] Multi-select months. Serialised to a CSV `months=`. */
  months?: string[];
  search?: string;
  /** [EDIT-COLUMNS 2026-09-28] Admin-picked extra payments-table columns. Sent as a CSV
   *  (`extraCols=a,b`) — Express drops array params past 20; the server validates every name. */
  extraCols?: string[];
}

export async function fetchPayments(params: FetchPaymentsParams = {}): Promise<PaymentsResponse> {
  const { extraCols, ...rest } = params;
  const q = monthsParams(rest);
  if (extraCols && extraCols.length) q.extraCols = extraCols.join(',');
  const { data } = await api.get<PaymentsResponse>('/payments', { params: q });
  return data;
}

/** [EDIT-COLUMNS 2026-09-28] The payments table's real column list (admin only; 403 otherwise). */
// [PAYMENT-MODE 2026-10-18] `label` = a readable name for a non-database entry (Subscription / Manual).
export interface PaymentTableColumn { name: string; type: string; label?: string }
export async function fetchPaymentColumns(): Promise<PaymentTableColumn[]> {
  const { data } = await api.get<{ columns: PaymentTableColumn[] }>('/payments/columns');
  return data.columns || [];
}

export async function fetchFilters(month?: string): Promise<FiltersResponse> {
  const { data } = await api.get<FiltersResponse>('/payments/filters', {
    params: month ? { month } : {},
  });
  return data;
}

// ── New Sales ──
// Live mirror of payments that carry new-sale revenue. Read-only — categorizing
// still happens on the Payments page; every row shown here is also persisted to
// the server's new_sales record store.

interface FetchNewSalesParams {
  account?: string;
  month?: string;
  /** [MULTI-MONTH 2026-07-15] Multi-select months. Serialised to a CSV `months=`. */
  months?: string[];
  search?: string;
}

export async function fetchNewSales(
  params: FetchNewSalesParams = {},
  // [TAB-INSTANT 2026-08-20] `prefetch: true` marks a cache-warming call. The server skips
  // this GET's fire-and-forget new_sales store sync for it (routes/newSales.ts), which is
  // what makes warming this endpoint read-only. Sent as a HEADER on purpose: headers are
  // not part of cacheKey, so the warmed entry still matches the page's own request.
  opts: { prefetch?: boolean } = {},
): Promise<NewSalesResponse> {
  const { data } = await api.get<NewSalesResponse>('/new-sales', {
    params: monthsParams(params),
    ...(opts.prefetch ? { headers: { 'X-Prefetch': '1' } } : {}),
  });
  return data;
}

export async function fetchNewSalesFilters(): Promise<NewSalesFiltersResponse> {
  const { data } = await api.get<NewSalesFiltersResponse>('/new-sales/filters');
  return data;
}

export async function fetchNewSalesSummary(params: { month?: string; months?: string[]; account?: string } = {}): Promise<NewSalesSummary> {
  const { data } = await api.get<NewSalesSummary>('/new-sales/summary', { params: monthsParams(params) });
  return data;
}

export interface InBankSummary {
  retention: number;
  recurring_sale: number;
  one_time_sale: number;
  recurring_upsell: number;
  one_time_upsell: number;
  pia_amount: number;
  row_total: number;
  /* [ROW-SPLIT 2026-08-19] row_total broken into the four buckets it is made of, for the
     ROW side of the In Bank Summary. Additive only — row_total itself is unchanged, and
     these four sum back to it, so the panel can never disagree with the tile. */
  row_recurring_sale: number;
  row_one_time_sale: number;
  row_recurring_upsell: number;
  row_one_time_upsell: number;
  /* ⚠ Unlike the four above, this is a SUBSET of `retention`, not a diversion from it —
     Team ROW's PM-side retention, which is still counted inside the USA Retention tile.
     So the ROW panel does not sum to row_total; only the four row_* buckets do. */
  row_pm_retention: number;
  /* [ROW-PIA 2026-08-25] Team ROW's PIA (either side: deal owner on Team ROW - Sales, or the
     managing PM on Team ROW - PM that month), for the ROW panel's PIA Amount box. ADDITIVE and
     a SUBSET of pia_amount, exactly as row_pm_retention is of retention. */
  row_pia_amount: number;
  // [ROW-RD 2026-08-21] Team ROW share of the three deduction figures, for the ROW panel.
  // ADDITIVE: their Overall-side counterparts above are unchanged and are NOT reduced by these.
  row_this_month_refunds_disputes: number;
  row_refunds_disputes: number;
  row_disputes_won_recovery: number;
  recovery: number;
  disputes_won: number;
  refunds_disputes: number;
  need_to_map?: number;
  total: number;
  amount_total: number;
  this_month_refunds_disputes: number;
  /* [3-ROW SUMMARY 2026-08-31] The two extra ROW figures the summary's USA / ROW rows need:
     Team ROW's share of ungated Gross In-Bank, and the won-dispute half of
     row_disputes_won_recovery on its own (the EOM formula uses won and recovery in different
     terms, so the bundled figure cannot be split back apart). `row_recovery` is the recovery
     half and has been on the payload since 2026-08-21. */
  row_amount_total: number;
  row_disputes_won: number;
  row_recovery: number;
  /* [SHOPIFY 2026-10-19, user] The Shopify box (mapped Shopify payouts) and Team ROW's share of it (the ROW tag — 0 for
     a real Shopify payout). Optional so an older server payload still types (reads as 0). */
  shopify_amount?: number;
  row_shopify_amount?: number;
}

export async function fetchInBankSummary(params: { month?: string; account?: string } = {}): Promise<InBankSummary> {
  const { data } = await api.get<InBankSummary>('/payments/in-bank-summary', { params });
  return data;
}

export async function updatePaymentWebsite(id: number, website: string): Promise<Payment> {
  const { data } = await api.put<Payment>(`/payments/${id}`, { website });
  return data;
}

export async function updatePaymentEmail(id: number, email: string): Promise<Payment> {
  const { data } = await api.put<Payment>(`/payments/${id}`, { email });
  return data;
}

export interface CreatePaymentInput {
  payment_date: string;       // ISO date or YYYY-MM-DD
  charge_id: string;
  amount: number | string;
  account_name: string;
  email?: string;
  website?: string;
  client_name?: string;
  description?: string;
  object_type?: string;
  invoice_number?: string;
  status?: string;
}

export async function createPayment(input: CreatePaymentInput): Promise<Payment> {
  const { data } = await api.post<Payment>('/payments', input);
  return data;
}

export async function updatePaymentMapping(id: number, fields: Record<string, any>): Promise<Payment> {
  const { data } = await api.put<Payment>(`/payments/${id}/mapping`, fields);
  return data;
}

// Who last changed this payment (mapping or inline website/email edit), resolved
// from the audit trail by their Google login. Drives the "Last updated by …" line
// in the Edit Mapping popup. All fields are null when the row was never edited.
export interface PaymentLastEdit {
  userName: string | null;
  userEmail: string | null;
  at: string | null;
}

export async function fetchPaymentLastEdit(id: number): Promise<PaymentLastEdit> {
  const { data } = await api.get<PaymentLastEdit>(`/payments/${id}/last-edit`);
  return data;
}

export interface DelayedPaymentRow {
  website: string | null;
  email: string | null;
  campaigns: string | null;
  type: string | null;
  pm: string | null;
  brand: string | null;
  amount: string | null;
  payment_type: string | null;
  billing_date: string | null;
  month: string | null;
  status: string | null;
  total_payment_received: string | null;
  recurring_retention: string | null;
  retention_status: string | null;
}

export async function fetchDelayedPayments(): Promise<{ rows: DelayedPaymentRow[]; size: number; lastRefresh: string | null }> {
  const { data } = await api.get('/delayed-payments');
  return data;
}

// Force the server to rebuild the delayed-payments cache from the DB right now
// (bypasses the background interval) and return the freshly loaded rows. Used by
// the manual Refresh button so a recent DB change shows up here within a second
// or two. (No Google Sheet is involved — this reads the inventory/payments tables.)
export async function refreshDelayedPayments(): Promise<{ rows: DelayedPaymentRow[]; size: number; lastRefresh: string | null }> {
  const { data } = await api.post('/delayed-payments/refresh');
  return data;
}

export interface EomTableRow {
  month: string | null;
  pm_inventory: string | null;
  pm_retention_achieved: string | null;
  retention_pct: string | null;
  recurring_new_sale: string | null;
  one_time_new_sale: string | null;
  total_new_sales: string | null;
  recurring_upsell: string | null;
  one_time_upsell: string | null;
  total_pm: string | null;
  refunds: string | null;
  disputes_won: string | null;
  net_seo_total: string | null;
}

export async function fetchEomTable(): Promise<{ rows: EomTableRow[]; size: number; lastRefresh: string | null }> {
  const { data } = await api.get('/eom-table');
  return data;
}

export interface RetentionFlowchart {
  initialExpected: number;
  expectedTotal: number;
  expectedRetained: number;
  expectedPending: number;
  atRiskTotal: number;
  atRiskExpected: number;
  atRiskRetained: number;
  retained: number;
  cancelled: number;
  exclusions: number;     // One-Time inventory total for the month
  somInventory: number;   // total inventory (all types) for the month
  // Leaf-node figures computed directly from the inventory tab (Recurring only).
  nodeExpectedPending: number;  // status Expected, Received = 0  → SUM(Amount)
  nodeExpectedAwaited: number;  // status Expected, Received > 0  → SUM(Pending Payment)
  nodeAtRiskExpected: number;   // status At - Risk Expected      → SUM(At-Risk Expected Payment)
  // [RETENTION-NODES 2026-06-23] At Risk parent + redefined At Risk Awaited + Discount node
  nodeAtRisk: number;           // status At - Risk               → SUM(Amount)  [At Risk parent node]
  nodeAtRiskAwaited: number;    // status At-Risk|At-Risk-Expected, Retention≠0 & Retention<Amount → SUM(Amount − Retention)
  discountTotal: number;        // inventory.discount over ALL Recurring rows (any status) → SUM(Discount)
  refundDisputeFalsePm: number; // refunds + non-won disputes, team='PM', month_status='FALSE' (subtracted IN Expected EOM)
  newSalesTotal: number;        // Recurring + One-Time New Sales (Payments In-Bank Summary) → added INTO Expected EOM
}

// PM table row — one field per tree node, plus Expected EOM Retention + the two %.
export interface PmRetentionRow {
  pmName: string;
  initialExpected: number;
  currentExpected: number;
  expectedPending: number;
  expectedAwaited: number;
  atRisk: number;
  atRiskExpected: number;
  atRiskAwaited: number;
  retained: number;
  cancelled: number;
  discount: number;
  expectedEOMRetention: number;
  currentRetentionPct: number;
  expectedRetentionPct: number;
}

export interface RetentionPipelineData {
  month: string;
  flowchart: RetentionFlowchart;
  pmRetention: PmRetentionRow[];
  expectedEOM: number;
  expectedEOMRetention: number;
  expectedEOMRetentionPct: number;
}

export async function fetchRetentionPipelineMonths(): Promise<string[]> {
  const { data } = await api.get<{ months: string[] }>('/retention-pipeline/months');
  return data.months;
}

export async function fetchRetentionPipeline(month: string): Promise<RetentionPipelineData> {
  const { data } = await api.get<RetentionPipelineData>('/retention-pipeline', { params: { month } });
  return data;
}

// Team Performance (Performance Dashboard | Team Ilma)

export interface TeamPerformanceRow {
  pm: string;
  effectiveInventory: number;
  expected: number;
  expectedRemaining: number;
  placedInBank: number;
  retentionPct: number;
  atRisk: number;
  retained: number;          // $ amount of Retained rows — still drives Expected Retention %
  retainedRetention: number; // [RETAINED-RETENTION 2026-07-10] SUM(retention) of Retained rows — the displayed "Retained" column
  atRiskExpected: number;
  cancelled: number;
  totalUpsell: number;
  recurringUpsell: number;
  oneTimeUpsell: number;
  // [PM-PERF-RD 2026-08-25] Refund/Dispute + Dispute Won per PM (My Inventory KPI rules).
  refundsDisputes: number;
  disputesWon: number;
  salesPendingPayment: number;
  currentEfficiencyPct: number | null;
  expectedEfficiencyPct: number | null;
  targetEfficiencyPct: number;
  requiredInBank: number | null;
  sparkline: number[];
}

export interface TeamTrendPoint {
  month: string;
  inventory: number;
  placed: number;
  retentionPct: number;
}

export interface TeamFunnelData {
  initialExpected: number;
  retained: number;
  expected: number;
  atRisk: number;
  atRiskExpected: number;
  cancelled: number;
  placedInBank: number;
}

export interface TeamAtRiskAccount {
  website: string;
  pm: string;
  amount: number;
  billingDate: number | null;
  status: string;
}

export interface TeamPerformanceData {
  month: string;
  team: string;
  rows: TeamPerformanceRow[];
  totals: TeamPerformanceRow;
  /* [UPSELL-HEALTH 2026-08-24] false = HubSpot could not be read, so every upsell figure
     here is 0 by absence rather than by fact; the three upsell columns render "—".
     Optional so a payload from an older server still reads as healthy. */
  upsellOk?: boolean;
  kpis: {
    totalInventory: number;
    totalPlaced: number;
    currentRetentionPct: number;
    expectedRetentionPct: number;
  };
  trend: TeamTrendPoint[];
  trendMonths: string[];
  sparklineMonths: string[];
  funnel: TeamFunnelData;
  atRiskAccounts: TeamAtRiskAccount[];
}

// [ALL-MONTHS 2026-07-30] `all` lifts the Jan-2026 dropdown floor — the Download-Report
// picker uses it so a senior can report on history (inventory goes back to Apr-2024).
// The page's own month dropdown calls this WITHOUT `all` and stays floored.
export async function fetchTeamPerformanceMonths(all = false): Promise<string[]> {
  const { data } = await api.get<{ months: string[] }>('/team-performance/months', {
    params: all ? { all: 1 } : {},
  });
  return data.months;
}

// The live PM teams (discovered from HubSpot), used to build the Performance tab
// bar dynamically. Each is a tab key + display label + page title.
export interface PerfTeamTab {
  key: string;
  label: string;
  title: string;
}

export async function fetchPerformanceTeams(): Promise<PerfTeamTab[]> {
  const { data } = await api.get<{ teams: PerfTeamTab[] }>('/team-performance/teams');
  return data.teams;
}

export async function fetchTeamPerformance(team: string, month: string): Promise<TeamPerformanceData> {
  const { data } = await api.get<TeamPerformanceData>('/team-performance', { params: { team, month } });
  return data;
}

// PM Leaderboard — cumulative rolling window, independent of the month filter.
export interface PmLeaderboardRow {
  pm: string;
  placedInventory: number;
  placedInBank: number;
  retentionPct: number;
}

export interface PmLeaderboardData {
  start: string;   // window start (YYYY-MM-DD, inclusive)
  end: string;     // window end   (YYYY-MM-DD, inclusive = today)
  team: string;
  rows: PmLeaderboardRow[];   // ranked by placedInBank, highest first
}

export async function fetchPmLeaderboard(team: string): Promise<PmLeaderboardData> {
  const { data } = await api.get<PmLeaderboardData>('/team-performance/leaderboard', { params: { team } });
  return data;
}

// ── Dialing Report (PM Video Meets & Calls) ──
// Per-PM dialing metrics for a single day, one table per live HubSpot PM team.
// Read-only; the source data lives in a separate schema written by an external
// Meet/calls sync. A GET, so it never touches the Application Logs.

export interface DialingMember {
  name: string;
  scheduled: number;
  happened: number;
  noShow: number;
  abandoned: number;
  rescheduled: number;
  spillover: number;
  impulsive: number;
  totalCalls: number;
  talktimeSeconds: number;
  dailyAvgSeconds: number;
  hasData: boolean;
}

export interface DialingTeamGrand {
  scheduled: number;
  happened: number;
  noShow: number;
  abandoned: number;
  rescheduled: number;
  spillover: number;
  impulsive: number;
  totalCalls: number;
  talktimeSeconds: number;
  dailyAvgSeconds: number;
}

export interface DialingTeam {
  key: string;
  label: string;             // "Team Ilma - PM"
  members: DialingMember[];
  grand: DialingTeamGrand;
}

export interface DialingReport {
  start: string;             // inclusive range start actually rendered (YYYY-MM-DD)
  end: string;               // inclusive range end (start === end is a single day)
  minDate: string | null;
  maxDate: string | null;
  availableDates: string[];  // dates with data — bounds the range picker
  teams: DialingTeam[];
  totals: {
    totalTalktimeSeconds: number;
    dailyAverageSeconds: number;
    personCount: number;
    activePersonCount: number;
  };
}

export type DialingDept = 'PM' | 'Sales';

export async function fetchDialingReport(
  start?: string,
  end?: string,
  dept: DialingDept = 'PM'
): Promise<DialingReport> {
  const { data } = await api.get<DialingReport>('/dialing-report', {
    params: { ...(start ? { start } : {}), ...(end ? { end } : {}), dept },
  });
  return data;
}

// My Sales API — live HubSpot Sales-Pipeline deals + sales-team refund/dispute KPIs.

export interface MySalesDeal {
  dealName: string;
  dealOwner: string;
  amount: number;
  paymentType: string;
  pipeline: string;
  companyUrl: string;
  email: string;
  creative: number;
  ranking: number;
  performance: number;
  tech: number;
  closeDate: string | null;
  // [DEACTIVATED-OWNER 2026-07-30] The owner has left HubSpot (resolved from the archived
  // owner list). `dealOwner` is still the plain name; the table renders a "(Deactivated)"
  // label beside it. Optional so an older/cached response without the field still parses.
  ownerInactive?: boolean;
  // [SECOND-MONTH 2026-09-17, user] Recurring deals: the website's Retention in the NEXT month's
  // inventory (0 = none), split by deal amount across that website's deals that month.
  // null = One-Time or that month has not started (both show "—"). Optional like ownerInactive.
  secondMonthPayment?: number | null;
  // [TEAM-NAME 2026-09-18, user] The Sales team the deal owner belongs to ("Team - Aman"), from the
  // live roster (the deal month's history for a departed rep). '' → the table shows "—".
  teamName?: string;
}

export interface MySalesSummary {
  recurringSales: number;
  oneTimeSales: number;
  totalNewSales: number;
  // [SALES-PIA 2026-09-18, user] Σ PIA deals for the month/rep — its own tile after Total New Sales,
  // and a + term in Net Sales. Optional: absent = an older server build, so the tile reads "—"
  // rather than a made-up $0.00 (the same rule the refund fields below follow).
  salesPia?: number;
  refundsDisputes: number;
  disputesWon: number;
}

export interface MySalesTrendPoint {
  month: string;       // YYYY-MM
  recurring: number;
  oneTime: number;
  // [SALES-PIA 2026-09-18, user] PIA deals closed that month. null/absent = older server → no PIA line.
  pia?: number | null;
  // [CHART REFUNDS 2026-09-17] Refunds + lost disputes that month (= the Refunds / Disputes tile).
  // null/absent = unavailable (older server or failed query) → no third line.
  refundsDisputes?: number | null;
  // [CHART TOOLTIP 2026-09-17] Disputes won that month (= the Disputes Won tile) — for Net Sales.
  disputesWon?: number | null;
}

// One underlying row behind the Refunds / Disputes KPI (for its hover breakdown).
export interface MySalesRefundDispute {
  website: string;
  salesRep: string;             // assignee_name ('' when unassigned)
  amount: number;
  type: 'Refund' | 'Dispute';
}

// [LEADS-PERF 2026-07-17] My Sales right-pane leads table (from SEO_Hubspot_Raw.Leads_Raw).
export interface MySalesLeadsPerfMetrics {
  sales: number;
  conversion: number;  // percent (0..100)
  inBank: number;
  atv: number;
}
// [OVERALL-BREAKDOWN 2026-09-29, user] The parts of the Overall Sales Net In-Bank, shown as their own
// columns: inBank = recurring + oneTime + pia − refundsDisputes + disputesWon. Optional because an
// older server (the seconds of a deploy) sends none — those cells then read "—", never $0.
export interface MySalesOverallMetrics extends MySalesLeadsPerfMetrics {
  recurring?: number;
  oneTime?: number;
  pia?: number;
  refundsDisputes?: number;   // positive amount deducted (refunds + disputes not won)
  disputesWon?: number;
}
export interface MySalesLeadsPerfRow extends MySalesLeadsPerfMetrics {
  month: string;       // 'YYYY-MM'  ('' for the Total row)
  leads: number;
  // Flat sales/conversion/inBank/atv = 'Leads to Sales' view (Leads_Raw). [OVERALL 2026-07-17]
  // overall = 'Overall Sales' view (HubSpot closed-won deals for the same month/rep).
  // [NET-IN-BANK 2026-09-16, user] overall.inBank = the Net Sales KPI for the month (can be
  // negative); overall.atv = that ÷ overall.sales.
  overall: MySalesOverallMetrics;
}
export interface MySalesLeadsPerf {
  title: string;                 // the selected rep, or 'All Sales Rep'
  rows: MySalesLeadsPerfRow[];   // 6 months, oldest -> newest
  total: MySalesLeadsPerfRow;
  // [SILENT-PARTIAL FIX 2026-08-15] False = the HubSpot leg failed, so every `overall.*` value
  // is a default 0, not a measurement — the 'Overall Sales' view must show "—". Optional so an
  // older server payload (no field) is read as healthy.
  overallOk?: boolean;
  // [DB-OUTAGE-FIX 2026-10-28, user] False = the Leads_Raw leg failed: every month's leads and the whole
  // 'Leads to Sales' view (and Overall's Conversion%, which divides by leads) is unknown → "—", and the
  // page asks again by itself. Absent (an older server) = healthy.
  leadsOk?: boolean;
}

// [SILENT-PARTIAL FIX 2026-08-15] Trust signal for the HubSpot-derived half of My Sales (the
// snapshot KPIs, the trend chart, the deals table, the "Overall Sales" view). When ok === false
// the server could not complete the paged HubSpot deal search, so those figures are UNKNOWN —
// they must be rendered as "—" with the banner, never as 0. Everything DB-backed (refunds /
// disputes, the month list, the Leads to Sales table) is unaffected and still accurate.
export interface MySalesDealsHealth {
  ok: boolean;
  stale: boolean;
  asOf: string | null;  // ISO time the served deals were fetched
  message: string;
}

export interface MySalesData {
  month: string;
  salesPerson: string;          // effective selected person ('' = All Sales) after access scoping
  defaultPerson: string;        // the user's default selection ('' = All Sales for admin/senior)
  canSelectAll: boolean;        // may the user pick "All Sales"? (admin/senior only)
  deals: MySalesDeal[];         // the user's full allowed scope for the month (client narrows by person/search)
  summary: MySalesSummary;
  refundsDisputesBreakdown: MySalesRefundDispute[]; // rows behind summary.refundsDisputes (hover breakdown)
  disputesWonBreakdown: MySalesRefundDispute[];     // rows behind summary.disputesWon (hover breakdown)
  months: string[];
  salesPeople: string[];        // the sales names this user may pick
  trend: MySalesTrendPoint[];   // last 6 months (oldest -> newest), static across the month filter
  // [LEADS-PERF 2026-07-17] 6 completed months (current excluded) of leads/sales/in-bank
  // for the selected rep (or all the dropdown's reps when "All Sales Rep").
  leadsPerf: MySalesLeadsPerf;
  // [SILENT-PARTIAL FIX 2026-08-15] Optional so an older cached/deployed server payload
  // (which has no such field) is treated as healthy rather than rendering a false alarm.
  dealsHealth?: MySalesDealsHealth;
}

/** [MULTI-MONTH 2026-07-15] `months` is now a list. Serialised to a CSV `months=`
 *  via monthsParams, same wire format as every other month-filtered endpoint. */
export async function fetchMySales(months?: string[], salesPerson?: string): Promise<MySalesData> {
  const { data } = await api.get<MySalesData>('/my-sales', {
    params: monthsParams({ months: months && months.length ? months : undefined, ...(salesPerson ? { salesPerson } : {}) }),
  });
  return data;
}

// [MYSALES-REPORT 2026-10-28, user] The Download button's data (server computeMySalesReport): the Leads to Sales | Overall
// Sales table of every active sales rep the user may see (admin / senior = all, team lead = his team, rep = himself),
// each exactly as the screen shows it for that rep, plus the total. Asked at the click, never kept in the GET cache.
export interface MySalesReportRep {
  name: string;
  perf: MySalesLeadsPerf;
}
export interface MySalesReport {
  months: string[];                 // the table's 6 completed months, oldest -> newest
  canSelectAll: boolean;            // admin / senior: the total is "All Sales Rep"; a team lead's is his team's
  reps: MySalesReportRep[];         // every active rep the user may see, A → Z (the file leaves out the ones with no data)
  total: MySalesLeadsPerf | null;   // the screen's All Sales Rep table; null when the user sees one rep only
  noRep: MySalesLeadsPerf | null;   // refunds / disputes inside `total` on no active rep (no assignee / not active)
}

export async function fetchMySalesReport(): Promise<MySalesReport> {
  // A time limit of its own: an uncached GET has none, and the Download menu waits on this.
  const { data } = await api.get<MySalesReport>('/my-sales/report', { timeout: 60_000 });
  return data;
}

// [SALES-PIPELINE 2026-10-07, user] Sales Pipeline tab — each rep's forecast board for the month.
export type PipelineStage = 'commit' | 'likely' | 'upside' | 'early';

export interface PipelineDeal {
  id: number;
  month: string;            // YYYY-MM
  website: string;
  email: string;
  paymentType: string;      // Recurring | One-Time
  amount: number;
  stage: PipelineStage;
  owner: string;
  addedBy: string | null;
  addedAt: string;          // ISO, UTC
  stageChangedAt: string;   // ISO, UTC
  hubspotCompanyId: string | null;
  /** [PAID-GREEN 2026-10-07] New Sale money (Recurring + One-Time mapped) received this month for the website. */
  paidAmount: number | null;
  /** [DECLINE 2026-10-07] Marked declined / refused. */
  declined: boolean;
  /** [CLOSE-DATE 2026-10-12] Tentative Close Date, YYYY-MM-DD; null on cards saved before it existed. */
  closeDate?: string | null;
}

/** [SUGGEST 2026-10-07] One HubSpot company / contact email offered while typing. */
export interface PipelineWebsiteSuggestion { website: string; companyName: string | null; owner: string | null; email: string | null; mine: boolean }
export interface PipelineEmailSuggestion { email: string; name: string | null; fromWebsite: boolean }

export async function fetchPipelineWebsiteSuggestions(q: string): Promise<PipelineWebsiteSuggestion[]> {
  const { data } = await api.get<{ items: PipelineWebsiteSuggestion[] }>('/sales-pipeline/suggest', { params: { field: 'website', q }, timeout: 15_000 });
  return data.items;
}

export async function fetchPipelineEmailSuggestions(q: string, website: string): Promise<PipelineEmailSuggestion[]> {
  const { data } = await api.get<{ items: PipelineEmailSuggestion[] }>('/sales-pipeline/suggest', { params: { field: 'email', q, ...(website ? { website } : {}) }, timeout: 15_000 });
  return data.items;
}

/** [CARD-ACTIVITY 2026-10-07] The Deal Owner's activity on one card for the card's month. `hubspot` is null when the
 *  website is not a HubSpot company (or HubSpot could not be read): the card then shows only the Google Meet line. */
export interface PipelineCardActivity {
  hubspot: { emails: number; calls: number } | null;
  meet: { held: number; scheduled: number };
}

export async function fetchPipelineActivity(month: string, salesPerson?: string): Promise<{ month: string; items: Record<number, PipelineCardActivity> }> {
  const { data } = await api.get<{ month: string; items: Record<number, PipelineCardActivity> }>('/sales-pipeline/activity', {
    params: { month, ...(salesPerson ? { salesPerson } : {}) },
    timeout: 60_000,
  });
  return data;
}

/** [DECLINE 2026-10-07] Mark a card declined (true) or undo it (false). */
export async function setPipelineDealDeclined(id: number, declined: boolean): Promise<PipelineDeal> {
  const { data } = await api.patch<{ item: PipelineDeal }>(`/sales-pipeline/${id}/decline`, { declined });
  return data.item;
}

/** [CARD-EDIT 2026-10-07] Change a saved card's amount, payment type and/or [CLOSE-DATE 2026-10-12] close date. */
export async function editPipelineDeal(id: number, changes: { amount?: string; paymentType?: string; closeDate?: string }): Promise<PipelineDeal> {
  const { data } = await api.patch<{ item: PipelineDeal }>(`/sales-pipeline/${id}`, changes);
  return data.item;
}

export interface PipelineBoard {
  ready: boolean;           // false until the deploy creates the tables
  month: string;
  currentMonth: string;
  months: string[];
  salesPerson: string;      // '' = All Sales Rep
  salesPeople: string[];
  canSelectAll: boolean;    // admin / senior
  me: string;               // the signed-in person's sales name (a rep's locked Deal Owner)
  ownerChoices: string[];   // admin / senior: reps they may add a deal for
  items: PipelineDeal[];
}

export async function fetchSalesPipeline(month?: string, salesPerson?: string): Promise<PipelineBoard> {
  const { data } = await api.get<PipelineBoard>('/sales-pipeline', {
    params: { ...(month ? { month } : {}), ...(salesPerson ? { salesPerson } : {}) },
  });
  return data;
}

export async function addPipelineDeal(body: {
  month: string; website: string; email: string; paymentType: string; amount: string; stage: PipelineStage; owner?: string;
  closeDate: string; // [CLOSE-DATE 2026-10-12] YYYY-MM-DD
}): Promise<PipelineDeal> {
  const { data } = await api.post<{ item: PipelineDeal }>('/sales-pipeline', body);
  return data.item;
}

export async function movePipelineDeal(id: number, stage: PipelineStage): Promise<PipelineDeal> {
  const { data } = await api.patch<{ item: PipelineDeal }>(`/sales-pipeline/${id}/stage`, { stage });
  return data.item;
}

// [LEAD-ASSIGNMENT 2026-10-13, user] Lead Assignment tab — the lead mailboxes' emails of one day, marked Leads / Not Leads.
export type LeadView = 'raw' | 'leads' | 'irrelevant';
export type LeadTag = 'Lead' | 'Not Lead';

export interface LeadRow {
  id: number;                 // Leads_Mailbox_Dump.id (also for the rows shown from the two tables)
  date: string;               // when the email came, IST 'YYYY-MM-DD HH:MM:SS'
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  preview: string;            // the start of the Prospect Email Copy (the whole text: fetchLeadEmailBody)
  website: string | null;
  phone: string | null;
  leadBy: string | null;
  /** [LEAD-GENERATORS 2026-10-13] From Team_Email_Marketing_Reporting_Structure by the Lead Generated By email. */
  leadByName?: string | null;
  teamManager?: string | null;
  teamLeader?: string | null;  // [TEAM-LEADER] the same table's Team Leader Name
  /** [LEADS-IDENTIFIED-FILL 2026-10-13] Leads Identified: Leads Tracker 2.0 "Mailbox (New)" columns (dates: UTC ISO). */
  status?: string | null;
  activity?: string | null;
  leadStatus?: string | null;
  nalStatus?: string | null;
  leadType?: string | null;
  createDate?: string | null;
  lastActivityDate?: string | null;
  confirmation?: string | null;
  businessType?: string | null; // [ASSIGN-PROCESS-AB 2026-10-14] set by the Assignment Process (null = not run yet)
  geography: string | null;   // the email's Country line
  tag: LeadTag | null;
  markedBy: string | null;
  markedAt: string | null;    // ISO, UTC
  source: string | null;      // "Lead Assignment tab" / "Leads Tracker 2.0 (assigned YYYY-MM-DD)"
  /** [IN-LEADS-TAG 2026-10-13] Raw Data: another email of the same conversation is already under Leads (a reply). */
  alreadyInLeads?: boolean;
  /** [LEAD-DATE 2026-10-13] The email's own IST day — the earliest day its lead may go to. */
  receivedDay?: string;
  /** [US-ROW 2026-10-13] US, USA, United States, Canada → 'US'; another country → 'ROW'; no country line → null. */
  region?: 'US' | 'ROW' | null;
  /** [PAGE-LIST 2026-10-19] Raw Data: the email's subject line — the Single page view's left list. */
  subject?: string;
  /** [DUPLICATE 2026-10-13] Raw Data: marked Duplicate (listed last). */
  duplicate?: boolean;
  /** [NAL-MARK 2026-10-13] Raw Data: marked NAL (red, listed last — after the duplicates). Never together with duplicate. */
  nal?: boolean;
  /** [NAL-NOTE 2026-10-22] Raw Data: why it is NAL — written in the box the NAL button opens (null: marked before 22 Oct 2026). */
  nalNote?: string | null;
  /** [RAW-STATUS 2026-10-13] Raw Data: what happened to the row, read from its conversation in the lead mailbox (server
   *  services/leadAssignment.ts rawStatus) — to-map (nobody has acted on it) · waiting (forwarded to the Leads Dept, or the
   *  team asked the lead generator something) · nal / duplicate (the team's or the Leads Dept's verdict) · in-leads ·
   *  reply (an answer inside a conversation) · not-lead. */
  kind?: RawKind | null;
  kindText?: string | null;   // their words: "NAL - Community website", "Forwarded to leads@rnf.tech", "Leads Dept: Duplicate lead"
  kindAt?: string | null;     // when, IST 'YYYY-MM-DD HH:MM:SS'
  unread?: boolean | null;    // still UNREAD in Gmail (null = not known)
  inTracker?: LeadTrackerHint | null; // to-map: the prospect is already in Leads Tracker 2.0 (most likely a duplicate)
  about?: string | null;      // a reply's conversation — the email it belongs to: "SEO Lead No- 60 Oct (12 Oct 14:30)"
}
export type RawKind = 'to-map' | 'waiting' | 'nal' | 'duplicate' | 'in-leads' | 'reply' | 'not-lead';
/** [RAW-STATUS] The prospect's latest Leads Tracker 2.0 row (assigned null = not assigned) and how many it has there. */
export interface LeadTrackerHint { row: number; assigned: string | null; assignee: string | null; rows: number }

export interface LeadDay {
  ready: boolean;             // false until the tables exist
  day: string;
  view: LeadView;
  counts: { raw: number; leads: number; irrelevant: number; ready?: number }; // [READY] Leads Tracker 2.0 rows assigned that day
  rows: LeadRow[];
  /** [DUP-AFTER 2026-10-14] Raw Mailbox Data only: the day's Leads Data rows with Lead Status Duplicate (website + sender) */
  afterAssignment?: { id: number; website: string | null; leadBy: string | null }[];
}

/** [READY 2026-10-14] Leads Identified → "Ready to be Assigned": a Leads Tracker 2.0 "Hubspot" tab row assigned that day
 *  (server listReadyDay, from Leads_Raw). Sheet values as they are. */
export interface ReadyRow {
  rowNumber: number;
  date: string | null; firstName: string | null; lastName: string | null; email: string | null; preview: string;
  website: string | null; phone: string | null; leadBy: string | null; businessType: string | null; statusCreation: string | null;
  assignee: string | null; assigneeEmail: string | null; assigneeHubspotId: string | null; nalStatus: string | null;
  assignedDate: string | null; leadType: string | null; leadByName: string | null; teamName: string | null; leadSource: string | null;
  teamManager: string | null; hubspotRecordId: string | null; dealsRecordId: string | null; saleAmount: string | null;
  geography: string | null; leadTypeNamed: string | null;
  rowStatus?: 'ROW' | 'Non ROW' | null; // [READY-ROW-STATUS 2026-10-14] the lead's ROW Status (null = not known)
  assigneeHold?: string | null; // [ASSIGNEE-HOLD 2026-10-16] why Add Assignee gave the row no name

  nalVerification?: string | null; // [NAL-VERIF 2026-10-14] the HubSpot company's NAL Verification Status
  // [READY-A-AO 2026-10-14] W–AO of the Leads Tracker Hubspot tab (AM "Lead Status" = leadStatusTab)
  leadsCategory?: string | null; linkedin?: string | null; city?: string | null; state?: string | null; country?: string | null;
  industry?: string | null; employees?: string | null; yearFounded?: string | null; linkedinDescription?: string | null;
  companyKeyword?: string | null; companySpecialities?: string | null; designation?: string | null; responseScore?: string | null;
  enrichmentStatus?: string | null; resubmissionDate?: string | null; resubmissionSource?: string | null;
  leadStatusTab?: string | null; assignmentStatus?: string | null; noteStatus?: string | null;
}
export interface ReadyDay { ready: boolean; day: string; rows: ReadyRow[] }
export async function fetchReadyDay(date: string): Promise<ReadyDay> {
  const { data } = await api.get<ReadyDay>('/lead-assignment/ready', { params: { date } });
  return data;
}

export interface LeadEmailBody { id: number; date: string; from: string; subject: string; text: string }

/** One day's rows of one view, with the three counts. Cached like any page load; the page drops its own copy before a
 *  background refresh (new emails arrive from the mailbox job, which no version number reports). */
export async function fetchLeadDay(date: string, view: LeadView): Promise<LeadDay> {
  const { data } = await api.get<LeadDay>('/lead-assignment', { params: { date, view } });
  return data;
}

export async function fetchLeadEmailBody(id: number): Promise<LeadEmailBody> {
  const { data } = await api.get<LeadEmailBody>(`/lead-assignment/${id}/body`);
  return data;
}

/** "Leads" (tag Lead + a copy in Leads_Identified) or "Not Leads" (tag Not Lead + a copy in Irrelevant_Leads).
 *  [LEAD-DATE 2026-10-13] `date` (Leads only): the day's Leads it goes to — the email's own day or later. */
/** [UNIQUE-SITE 2026-10-14] sameSite: that day's Leads Data already had the website — 'replaced' = this newer email is shown in
 *  its place, 'older' = the one there is newer and stays shown (Leads Data keeps one row per website). */
/** [LEAD-WINS 2026-10-27] replacedNal = this person's NAL email(s) of the same website that day, replaced by this lead (out of Raw) */
export type MarkResult = { id: number; tag: LeadTag; countedDate: string; sameSite?: 'replaced' | 'older' | null; replacedNal?: number[] };
export async function markLeadEmail(id: number, tag: LeadTag, date?: string): Promise<MarkResult> {
  const { data } = await api.post<MarkResult>(
    `/lead-assignment/${id}/${tag === 'Lead' ? 'lead' : 'not-lead'}`, tag === 'Lead' && date ? { date } : {});
  return data;
}

// [MAIL-CONTENT 2026-10-13] The whole email: the conversation we hold, its HTML (made safe on the server, inline images
// built in), and its files — kept in our database, so the mailbox is not needed to read it.
export interface MailFile { id: number; filename: string | null; mime: string; size: number; inline: boolean; kept: boolean; image: boolean }
export interface MailMessage {
  id: number;
  date: string;                 // IST 'YYYY-MM-DD HH:MM:SS'
  fromName: string | null;
  fromEmail: string | null;
  to: string | null;
  cc: string | null;
  subject: string;
  html: string | null;          // a whole document for a sandboxed frame; null → `text`
  text: string;
  files: MailFile[];
  contentStatus: string | null; // ok / gone / error / null (not collected yet)
  sent?: boolean;               // [TEAM-REPLIES 2026-10-13] sent BY the lead mailbox (the team's reply)
}
export interface MailThread { id: number; messages: MailMessage[] }

/** Never cached in the app (isCacheable): one email with its pictures can weigh a few MB. */
export async function fetchLeadEmailThread(id: number): Promise<MailThread> {
  const { data } = await api.get<MailThread>(`/lead-assignment/${id}/email`, { timeout: 60_000 });
  return data;
}

/** A kept file: `view` = show a picture in place (the server only allows plain image types). [ATTACH-IN-MAIL 2026-10-13]
 *  The page only ever shows pictures with it — other files are listed as text, never downloaded. */
export const leadFileUrl = (fileId: number, view = false) => `/api/lead-assignment/files/${fileId}${view ? '?view=1' : ''}`;

/** [ASSIGN-PROCESS-AB 2026-10-14] The Assignment Process on one day's Leads Data — A = the ROW rows, B = the other rows (the
 *  Leads Tracker sheet's dates + status step; server services/leadProcess.ts). Admins only. */
/** [DATA-STATUS-FETCH 2026-10-19] AB = "Data Status Fetch": every row of the day in one run; `parts` = how many of the processed
 *  rows were ROW rows (A's part) and other rows (B's part). */
export interface AssignmentProcessResult {
  day: string; kind: 'A' | 'B' | 'AB'; rows: number; processed: number; parts?: { A: number; B: number }; skipped?: number; // [NO-DETAILS-SKIP] no email + no website yet
  found: { website: number; email: number; none: number }; leadStatus: Record<string, number>; hubspotCalls: number; ms: number;
}
export async function runAssignmentProcess(date: string, kind: 'A' | 'B' | 'AB'): Promise<AssignmentProcessResult> {
  const { data } = await api.post<AssignmentProcessResult>('/lead-assignment/process', { date, kind }, { timeout: 180_000 });
  return data;
}

/** [ASSIGNEE-C 2026-10-14] C = Add Assignee on one day's Ready to be Assigned (the Leads Tracker's add_assignee(); server
 *  services/leadAssignee.ts): K Assignee, L Assignee (Email ID), M Assignee (HubSpot ID). Admins only. */
/** [ASSIGNEE-HOLD 2026-10-16] A row Add Assignee gave NO name: its email CCs a HubSpot owner outside the row's list. */
export interface AssigneeHoldRow {
  id: number; row: boolean; website: string | null; email: string | null; firstName: string | null;
  /** [CC-NOT-ACTIVE 2026-11-03] `why` = what keeps the person out of the lead's Active list */
  people: { name: string; email: string; team: string; why?: 'inactive' | 'not-sales' | 'other-side' | 'no-user' }[]; reason: string;
}
export interface AddAssigneeResult {
  day: string; rows: number; had: number; assigned: number; mail: number; random: number; noNames: number;
  row: { rows: number; names: Record<string, number> }; nonRow: { rows: number; names: Record<string, number> };
  pools: { row: string[]; nonRow: string[]; leftOut: string[] }; ms: number;
  held?: number; holds?: AssigneeHoldRow[]; released?: number; // [ASSIGNEE-HOLD 2026-10-16]
  /** [ASSIGN-BY-TIME 2026-10-19] the part the server worked on ('ROW' before 5 PM IST) and the day's Ready rows in all */
  part?: AssignPart; dayRows?: number;
}
/** [ASSIGN-BY-TIME 2026-10-19] Assign to HubSpot's rows: 'ROW' = ROW Status ROW only (before 5 PM IST), 'all' = every row left;
 *  'auto' = the server's clock decides */
export type AssignPart = 'ROW' | 'all';
/** [ASSIGNEE-NAMES 2026-10-16] Lead Management → Assignee Names: the LIVE HubSpot Sales people by their live team, Active or not
 *  (Add Assignee uses only the Active ones). `stored` false = nothing saved yet (the boxes show today's lists). Admins only. */
export interface AssigneeNamesView {
  stored: boolean;
  teams: { team: string; side: 'ROW' | 'US'; people: { id: string; name: string; email: string | null; active: boolean }[] }[];
}
export async function fetchAssigneeNames(): Promise<AssigneeNamesView> {
  const { data } = await api.get<AssigneeNamesView>('/lead-assignment/assignee-names');
  return data;
}
export async function saveAssigneeName(id: string, active: boolean): Promise<AssigneeNamesView> {
  const { data } = await api.post<AssigneeNamesView>('/lead-assignment/assignee-names', { id, active });
  return data;
}

/** [ASSIGNEE-HOLD] `release` = held rows to give a name the usual way anyway ("Assign them the usual way"). */
export async function runAddAssignee(date: string, release?: number[], part?: AssignPart | 'auto'): Promise<AddAssigneeResult> {
  const body = { date, ...(release?.length ? { release } : {}), ...(part ? { part } : {}) }; // [ASSIGN-BY-TIME] part
  const { data } = await api.post<AddAssigneeResult>('/lead-assignment/assignee', body, { timeout: 120_000 });
  return data;
}

/** [ENRICH-DATA] [ASSIGN-HUBSPOT 2026-10-14] The two long steps run on the server in the background: start → follow the job. */
export type LeadJobKind = 'enrich' | 'hubspot' | 'leadsraw';
export interface EnrichResult {
  day: string; rows: number; done: number; errors: number; notFound: number; batches: number; emailsFixed?: number; ms: number;
  stopped?: boolean; left?: number; // [ASSIGN-ALL 2026-10-19] Stop in Assign to HubSpot's run, during Enrich Data
}
export interface AssignHubspotResult {
  day: string; rows: number; assigned: number; failed: number; emailsFixed?: number; // [EMAIL-FIX]
  skipped: { assigned: number; noWebsite: number; noAssignee: number; noEmail: number };
  notes: { rows: number; added: number; failed: number }; failures: { id: number; website: string; why: string }[]; ms: number;
  /** [GEOGRAPHY-A 2026-10-14] the HubSpot Geography step (blank companies only) — or why it could not run */
  geography?: { companies: number; alreadySet: number; merged: number; written: Record<string, number>; leftBlank: number; notStuck: number; team: string[];
    corrected?: Record<string, number> /* [GEO-LATEST 2026-10-15] set Geographies changed to the latest lead's */ } | { error: string };
  /** [E-FAST 2026-10-16] Stop was pressed: the run ended after the rows in hand (each of them assigned + its note) */
  stopped?: boolean;
  /** [E-FAST] how many times HubSpot asked us to slow down (it waited and went on) */
  slowDowns?: number;
  /** [E-FAST] stopped: the rows that run had in hand and did not get to (they wait for the next run) */
  left?: number;
  /** [ASSIGN-ALL 2026-10-19] the run enriched first: what Enrich Data did; stoppedAt 'enrich' = stopped before HubSpot (nothing sent) */
  enrich?: EnrichResult;
  stoppedAt?: 'enrich';
  part?: AssignPart; // [ASSIGN-BY-TIME 2026-10-19] the rows the run worked on
  /** [ASSIGN-LEADS-RAW 2026-10-19] the run ends with Transfer to Leads Raw (every Assigned row of the day) — or why it could not */
  leadsRaw?: LeadsRawResult | { error: string };
}
/** [LEADS-RAW-APP 2026-10-14] Transfer to Leads Raw */
export interface LeadsRawResult { day: string; rows: number; assigned: number; transferred: number; added: number; updated: number; notAssigned: number; ms: number }
export interface LeadJob {
  id: number; kind: LeadJobKind; day: string; by: string | null; state: 'running' | 'done' | 'failed'; startedAt: string; endedAt: string | null;
  progress: { phase: string; done: number; total: number }; result: EnrichResult | AssignHubspotResult | LeadsRawResult | null; error: string | null;
  /** [E-FAST 2026-10-16] Stop was pressed — the step ends after the rows in hand */
  stopRequested?: boolean;
  part?: AssignPart; // [ASSIGN-BY-TIME 2026-10-19] Assign to HubSpot's rows
}
/** [ASSIGN-ALL 2026-10-19] enrichFirst (Assign to HubSpot only): the server's run does Enrich Data, then Assign to HubSpot.
 *  [ASSIGN-BY-TIME] part: the rows Add Assignee worked on (ROW before 5 PM IST). */
export async function startLeadJob(kind: LeadJobKind, date: string, ids?: number[], opts: { enrichFirst?: boolean; part?: AssignPart | 'auto' } = {}): Promise<LeadJob> {
  const body = { date, ...(ids ? { ids } : {}), ...(opts.enrichFirst ? { enrichFirst: true } : {}), ...(opts.part ? { part: opts.part } : {}) };
  const { data } = await api.post<LeadJob>(`/lead-assignment/${kind}`, body, { timeout: 60_000 });
  return data;
}
export async function fetchLeadJob(kind: LeadJobKind): Promise<LeadJob | null> {
  const { data } = await api.get<{ job: LeadJob | null }>('/lead-assignment/job', { params: { kind } });
  return data.job;
}
/** [E-FAST 2026-10-16, user] "do it have the option to stop if midway": asks the running Assign to HubSpot to stop after the rows in hand. */
export async function stopLeadJob(kind: LeadJobKind): Promise<LeadJob> {
  const { data } = await api.post<LeadJob>('/lead-assignment/job/stop', { kind }, { timeout: 30_000 });
  return data;
}

/** [LEAD-GEN-REPORT 2026-10-14] The Lead Generator Report of one day (server leadGeneratorReport) — every person with the page. */
export interface ReportGenerator { key: string; leadBy: string; name: string | null; team: string | null; unique: number; nal: number; duplicate: number; total: number }
export interface LeadReport {
  /** [GEN-RANGE 2026-10-27] the report's dates, both in (one day: from = to); day = from */
  day: string; from?: string; to?: string; total: number; row: number; us: number;
  /** [GEN-KPIS 2026-10-15] nal = the day's Raw Mailbox Data emails marked NAL */
  nal?: number;
  /** [GEN-KPIS-2 2026-10-15] the day's Ready to be Assigned rows and their ROW Status split (us = every row not ROW) */
  ready?: { total: number; row: number; us: number };
  status: { fresh: number; regenerated: number; duplicate: number; notYet: number };
  generators: { row: ReportGenerator[]; us: ReportGenerator[] };
  /** [GEN-NAL-RECORDS 2026-10-22] the "NAL records" tab: per person, the websites of their NAL emails of the day (US + ROW) */
  nalRecords?: NalPerson[];
}
/** [GEN-NAL-WHY 2026-10-22] notes[i] = the NAL note of sites[i] (null: marked before the note) */
export interface NalPerson { key: string; leadBy: string; name: string | null; team: string | null; sites: string[]; notes?: (string | null)[] }
/** [GEN-RANGE 2026-10-27] every day from `from` to `to` (both in) */
export async function fetchLeadReport(from: string, to: string = from): Promise<LeadReport> {
  const { data } = await api.get<LeadReport>('/lead-assignment/report', { params: { from, to } });
  return data;
}

/** [TEAM-SAMEER 2026-10-15, user] The Team Sameer Report tab: per lead generator of Team Sameer, their Leads_Raw rows assigned
 *  from..to (both days in), the NAL ones among them ("NAL" keyword or NAL Verification Status "NAL Accepted") and
 *  Final Count = leads − nal; rows by Final Count, high to low.
 *  [TEAM-SAMEER-DUP 2026-10-16, user] + duplicate = their Lead Management Leads Data rows with Lead Status Duplicate in the
 *  range; Final Count = leads − nal − duplicate.
 *  [TEAM-SAMEER-ONE-DUP 2026-10-27, user] the Duplicate comes off once, before the dates (genDuplicate; the second copy
 *  `duplicate` is gone): Count for Assignment (genFinal) = genUnique − genNal, Final Count = leads − nal. */
export interface TeamSameerRow {
  key: string; name: string | null; email: string; leads: number; nal: number; final: number;
  /** [TEAM-SAMEER-NAMED 2026-10-19] the Final Count by Leads_Raw's Lead Type (named lead / No name lead): named + noName = final */
  named: number; noName: number;
  /** [TEAM-SAMEER-GROUPS 2026-10-15] the lead generators table's Team Leader Name (null = not in it) and ROW Status (ROW → 'ROW', else 'US') */
  team: string | null; region: 'ROW' | 'US';
  /** [TEAM-SAMEER-GEN 2026-10-27] the Lead Generator Report's Unique (websites) · NAL · Duplicate of the same dates;
   *  [TEAM-SAMEER-ONE-DUP] genFinal = Count for Assignment = genUnique − genNal */
  genUnique: number; genNal: number; genDuplicate: number; genFinal: number;
  /** [TEAM-SAMEER-NOT-ASSIGNED 2026-10-27] of Count for Assignment less its Duplicate: the leads not in Leads Raw yet (waiting for Assign to HubSpot) */
  notAssigned: number;
}
export interface TeamSameerReport { from: string; to: string; rows: TeamSameerRow[]; total: { leads: number; nal: number; final: number; named: number; noName: number } }
export async function fetchTeamSameerReport(from: string, to: string): Promise<TeamSameerReport> {
  const { data } = await api.get<TeamSameerReport>('/team-sameer-report', { params: { from, to } });
  return data;
}

/* [LGP 2026-10-15, user] Lead Generator Performance — the Apps Script dashboard's five pages on our database
   (server services/leadGenPerformance.ts; shapes = the script's own responses). Every read asks the server again
   (invalidateLgp first): Leads_Raw changes without a version number the app's GET cache could follow. */
export type LgpMarket = '' | 'us' | 'row';
/** [LGP-DUP 2026-10-16, user] duplicate = the person's Lead Management Leads Data rows with Lead Status Duplicate in the range;
 *  final = total − nal − duplicate (the bonus follows final).
 *  [LGP-NO-DUP 2026-10-27, user] no duplicate any more ("the duplicate was never gone to the lead row table"): final = total − nal
 *  (= the Team Sameer Report's Final), the bonus follows it. */
export interface LgpLeadsRow { team: string; name: string; email: string; market: LgpMarket; total: number; nal: number; final: number; bonus: number }
export interface LgpLeadsReport {
  heading: string; start: string; end: string; teams: string[]; rows: LgpLeadsRow[];
  totals: { total: number; nal: number; final: number; bonus: number };
  meta: { people: number; markets: { us: number; row: number; none: number }; scannedRows: number; matchedRows: number; dataAsOf: string; version: string };
}
export interface LgpMonthCell { leads: number; sales: number; conversion: number; costInr: number; costUsd: number; bank: number }
export interface LgpMonthlyReport {
  months: { key: number; label: string; iso: string }[]; teams: string[];
  /** sideMonths [LGP-SIDES 2026-10-15]: only for a person on BOTH sides ("US + ROW") — each side's own figures */
  rows: { team: string; name: string; market: string; months: LgpMonthCell[]; sideMonths?: { us: LgpMonthCell[]; row: LgpMonthCell[] } }[];
  teamTotals: Record<string, LgpMonthCell[]>; totals: LgpMonthCell[];
  fx: { rate: number; source: string; asOf: string | null; stale: boolean };
  meta: { people: number; activeOnly: boolean; skippedInactive: number; skippedUnknown: number; scannedRows: number; dataAsOf: string; version: string };
}
export interface LgpNalRow { day: number; aDay: number; a: string; email: string; website: string; gen: string; biz: string; assignee: string; status: string; o: string; name: string; team: string; source: string }
export interface LgpNalReport { heading: string; start: string; end: string; rows: LgpNalRow[]; meta: { records: number; totalNal: number; scannedRows: number; dataAsOf: string; version: string } }
export interface LgpRosterRow { row: number; name: string; email: string; leader: string; manager: string; dupe: string; status: string; rowStatus: string }
export interface LgpRosterColumn { key: 'name' | 'email' | 'leader' | 'manager' | 'status' | 'rowStatus'; col: string; label: string; editable: boolean; type: 'text' | 'email' | 'select' }
export interface LgpRosterReport {
  columns: LgpRosterColumn[]; options: Record<string, string[]>; rows: LgpRosterRow[];
  meta: { people: number; rowsWithData: number; skippedNoStatus: number; statuses: Record<string, number>; leaders: Record<string, number>; managers: Record<string, number>; rowStatuses: Record<string, number>; version: string };
}
export interface LgpBonusRule { from: number; to: number | null; amount: number }
export interface LgpBonusRules { rules: LgpBonusRule[]; isDefault: boolean; defaults: LgpBonusRule[]; maxRules: number; version: string }
export interface LgpMember { name: string; email: string; leader: string; manager: string; status: string; rowStatus: string }
const LGP = '/lead-generator-performance';
const invalidateLgp = () => invalidateGetCacheByPrefix([LGP]);
export async function fetchLgpLeads(from: string, to: string): Promise<LgpLeadsReport> {
  invalidateLgp();
  return (await api.get<LgpLeadsReport>(`${LGP}/leads`, { params: { from, to } })).data;
}
export async function fetchLgpMonthly(month: string): Promise<LgpMonthlyReport> {
  invalidateLgp();
  return (await api.get<LgpMonthlyReport>(`${LGP}/monthly`, { params: { month } })).data;
}
export async function fetchLgpNal(from: string, to: string): Promise<LgpNalReport> {
  invalidateLgp();
  return (await api.get<LgpNalReport>(`${LGP}/nal`, { params: { from, to } })).data;
}
export async function fetchLgpRoster(): Promise<LgpRosterReport> {
  invalidateLgp();
  return (await api.get<LgpRosterReport>(`${LGP}/roster`)).data;
}
export async function checkLgpRosterEmail(email: string): Promise<{ exists: boolean; row?: number; name?: string; hasStatus?: boolean; status?: string }> {
  invalidateLgp();
  return (await api.get(`${LGP}/roster/email`, { params: { email } })).data;
}
export async function saveLgpRosterValue(row: number, email: string, key: string, value: string): Promise<LgpRosterReport> {
  return (await api.put<LgpRosterReport>(`${LGP}/roster/${row}`, { email, key, value })).data;
}
export async function addLgpRosterMember(member: LgpMember): Promise<LgpRosterReport> {
  return (await api.post<LgpRosterReport>(`${LGP}/roster`, { member })).data;
}
export async function fetchLgpBonus(): Promise<LgpBonusRules> {
  invalidateLgp();
  return (await api.get<LgpBonusRules>(`${LGP}/bonus`)).data;
}
export async function saveLgpBonus(rules: { from: string; to: string; amount: string }[]): Promise<LgpBonusRules> {
  return (await api.put<LgpBonusRules>(`${LGP}/bonus`, { rules })).data;
}
export async function resetLgpBonus(): Promise<LgpBonusRules> {
  return (await api.post<LgpBonusRules>(`${LGP}/bonus/reset`)).data;
}
export async function fetchLgpStatus(): Promise<{ version: string; dataAsOf: string | null }> {
  invalidateLgp();
  return (await api.get(`${LGP}/status`)).data;
}

/** [DT-DAILY 2026-10-16] The DT Daily Report tab (server services/dtDailyReport.ts — the Apps Script "SEO Daily Leads Assignment
 *  Report" on our server). Windows are [Today, This Week, This Month, Previous Month]; every number array follows that order. */
export interface DtDailyView {
  leadAssignment: { windows: string[]; totals: number[]; sources: { name: string; vals: number[] }[] };
  managers: { name: string; total: number[]; freelancer: number[]; inhouse: number[] }[];
  managerTotal: number[];
}
export interface DtDailyCard { team: string; members: { name: string; count: number }[]; total: number }
export interface DtDailyReport {
  anchorDate: string; today: string; todayLabel: string; windows: string[]; windowDates: string[]; avgBiz: { week: number; month: number };
  dt: DtDailyView; row: DtDailyView; advisorTeams: DtDailyCard[]; bottomTeams: DtDailyCard[];
  version: string; firstDay: string | null; rows: number; teamsLive: boolean;
}
const DT_DAILY = '/dt-daily-report';
/** always asked fresh (the page reloads only when the server's version moved, so a cached answer is never wanted) */
export async function fetchDtDailyReport(date?: string): Promise<DtDailyReport> {
  invalidateGetCacheByPrefix([DT_DAILY]);
  return (await api.get<DtDailyReport>(DT_DAILY, { params: date ? { date } : {} })).data;
}
export async function fetchDtDailyStatus(): Promise<{ version: string; today: string }> {
  invalidateGetCacheByPrefix([DT_DAILY]);
  return (await api.get(`${DT_DAILY}/status`)).data;
}
/** [DT-LEADS-ANALYSIS 2026-10-29] The Leads Analysis popup: every month (newest first), US + ROW. assigned = that month's This Month
 *  card · named + noName = assigned · nal = the NAL word, not NAL Accepted · totalNal = nal + nalAccepted · final = assigned − totalNal */
export interface DtLeadsAnalysisSide { assigned: number; named: number; noName: number; nal: number; nalAccepted: number; totalNal: number; final: number }
export interface DtLeadsAnalysisMonth { month: string; label: string; us: DtLeadsAnalysisSide; row: DtLeadsAnalysisSide }
export interface DtLeadsAnalysis { months: DtLeadsAnalysisMonth[]; today: string; version: string }
export async function fetchDtLeadsAnalysis(): Promise<DtLeadsAnalysis> {
  invalidateGetCacheByPrefix([DT_DAILY]);
  return (await api.get<DtLeadsAnalysis>(`${DT_DAILY}/leads-analysis`)).data;
}

/** [UNDO-IRRELEVANT 2026-10-13] Irrelevant Leads → back to Raw Data (to be decided again). */
export async function undoLeadNotLead(id: number): Promise<{ id: number; backTo: 'raw'; day: string }> {
  const { data } = await api.post<{ id: number; backTo: 'raw'; day: string }>(`/lead-assignment/${id}/undo-not-lead`);
  return data;
}

/** [UNDO-LEAD 2026-10-14] Leads Identified → back to Raw Mailbox Data ("if someone has mistakenly marked it as lead"). */
export async function undoLeadToRaw(id: number): Promise<{ id: number; backTo: 'raw'; day: string }> {
  const { data } = await api.post<{ id: number; backTo: 'raw'; day: string }>(`/lead-assignment/${id}/undo-lead`);
  return data;
}

/** [DUPLICATE 2026-10-13] Mark a Raw Data email Duplicate: it stays in Raw Data, listed last. */
/** [DUPLICATE-TOGGLE] [NAL-MARK 2026-10-13] A Raw Data mark on (the row stays, listed last) or off (back in its place).
 *  Setting one mark clears the other. */
export type LeadMark = 'duplicate' | 'nal';
/** [NAL-ONE-PER-SITE 2026-10-15] moved = older NAL emails of the same website (that day) the server moved to Not Lead */
/** [NAL-NOTE 2026-10-22] note = why it is NAL — the server saves a new NAL mark only with it */
/** [LEAD-WINS 2026-10-27] replacedByLead = the website is already this person's Lead that day: the NAL email was replaced (out of Raw) */
export type RawMarkResult = { id: number; mark: LeadMark; on: boolean; moved?: number[]; replacedByLead?: boolean };
export async function setLeadMark(id: number, mark: LeadMark, on: boolean, note?: string): Promise<RawMarkResult> {
  const { data } = await api.post<RawMarkResult>(`/lead-assignment/${id}/${on ? '' : 'undo-'}${mark}`,
    note !== undefined ? { note } : undefined);
  return data;
}

// Sales Scoreboard API — roster-wide sales leaderboard over the last 6 completed
// months (Leads_Raw + HubSpot roster/role), with admin-editable tier levers.

export type ScoreboardTier = 'Elite Class' | 'Tier B' | 'Tier C';

export interface ScoreboardLevers {
  eliteMonthlyInBank: number; // $
  eliteConversion: number;    // %
  monthsInPeriod: number;
}

export interface ScoreboardMonth {
  month: string;   // YYYY-MM
  leads: number;
  sales: number;
  inBank: number;
}

export interface ScoreboardRow {
  rank: number;
  seller: string;
  role: 'Team Leader' | 'Individual Contributor';
  leads: number;
  sales: number;
  inBank: number;      // [NET-IN-BANK 2026-09-16] NET: deals − Sales refunds/lost disputes + disputes won
  monthlyInBank: number;
  atv: number;
  avgMoConv: number;   // %
  tier: ScoreboardTier;
  monthly: ScoreboardMonth[];   // 6 window months, oldest → newest (hover breakdown)
}

export interface ScoreboardRoster {
  reps: number;
  leads: number;
  sales: number;
  inBank: number;
  monthlyInBank: number;
  atv: number;
  avgMoConv: number;
}

export interface SalesScoreboardData {
  rows: ScoreboardRow[];
  roster: ScoreboardRoster;
  levers: ScoreboardLevers;
  months: string[];    // window, oldest → newest (YYYY-MM)
}

/**
 * [PERIOD-PILL 2026-08-14] Which window the board covers — the two sides of the header pill.
 * 'last6' = the last 6 COMPLETED months (default). 'current' = the effective current month,
 * which is PARTIAL until month end.
 *
 * [MONTH-PICKER 2026-09-02] 'current' now means the SINGLE-MONTH board and the month travels
 * with it — the pill's right side is labelled "Monthly Scoreboard" and the header dropdown says
 * which month. Omitting the month still means the effective current month.
 */
export type ScoreboardPeriod = 'last6' | 'current';

export async function fetchSalesScoreboard(
  period: ScoreboardPeriod = 'last6',
  // [MONTH-PICKER 2026-09-02] Pass a month ONLY to name one other than the effective current
  // month. `undefined` is dropped from both the query string AND cacheKey() above, so the
  // default monthly view is the exact request the idle prefetch warms ('current', no month) and
  // still opens from cache. Passing the current month explicitly would be correct but would
  // miss that warmed entry.
  month?: string,
): Promise<SalesScoreboardData> {
  // Sent always (not only for 'current') so each period gets its own SWR cache entry and
  // flipping the pill back is instant instead of refetching.
  const { data } = await api.get<SalesScoreboardData>('/sales-scoreboard', { params: { period, month } });
  return data;
}

/** Admin/senior only — update the tier bars FOR ONE PERIOD; returns that period's recomputed
 *  board. The period travels with the write because the two sides keep independent bars.
 *  [MONTH-PICKER 2026-09-02] `month` changes nothing about what is saved (all months share the
 *  single-month period's bars) — it only makes the returned board the one on screen. */
export async function updateScoreboardLevers(
  levers: { eliteMonthlyInBank: number; eliteConversion: number },
  period: ScoreboardPeriod = 'last6',
  month?: string,
): Promise<SalesScoreboardData> {
  const { data } = await api.put<SalesScoreboardData>('/sales-scoreboard/levers', { ...levers, period, month });
  return data;
}

// Inventory API

interface FetchInventoryParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  // The toolbar filters are multi-select, so each accepts an array (a bare string
  // still works for any single-value caller). Arrays are sent as repeated query
  // keys (month=a&month=b) which the backend matches with IN(...).
  month?: string | string[];
  type?: string | string[];
  pm?: string | string[];
  brand?: string | string[];
  status?: string | string[];
  // [AM-NAMES 2026-09-04, user] The AM toolbar filter is SINGLE-select by the user's ruling
  // — one name, not an array — so this is a plain string. It asks "which rows is this AM
  // on", and the server matches the whole delimited name inside the ', '-joined column.
  am?: string;
  search?: string;
}

export async function fetchInventory(params: FetchInventoryParams = {}): Promise<InventoryResponse> {
  const { data } = await api.get<InventoryResponse>('/inventory', {
    params,
    // Serialize arrays as repeated keys (`month=a&month=b`) and drop empty
    // values so an unselected filter sends nothing at all.
    paramsSerializer: (p: Record<string, unknown>) => {
      const sp = new URLSearchParams();
      Object.entries(p).forEach(([key, val]) => {
        if (Array.isArray(val)) {
          val.forEach((item) => { if (item !== undefined && item !== null && item !== '') sp.append(key, String(item)); });
        } else if (val !== undefined && val !== null && val !== '') {
          sp.append(key, String(val));
        }
      });
      return sp.toString();
    },
  });
  return data;
}

// `month` scopes the Type/Brand/Status dropdowns to the value(s) in view. Pass
// one month or several (the toolbar filter is multi-select); omit for the full
// cross-month lists (search mode). Arrays serialise as repeated `?month=` params.
export async function fetchInventoryFilters(
  month?: string | string[],
  opts?: { allMonths?: boolean; excludeTypes?: string[] },
): Promise<InventoryFiltersResponse> {
  const months = Array.isArray(month) ? month : month ? [month] : [];
  const { data } = await api.get<InventoryFiltersResponse>('/inventory/filters', {
    // [ALL-MONTHS 2026-07-30] `allMonths` lifts the Jan-2026 dropdown floor from the
    // returned `months` ONLY — used by the download picker so history is exportable.
    // [PM-FILTER-PAST 2026-10-26, user] `excludeTypes` = the types the tab hides; a past month's
    // PM filter then lists only the names on the rows the tab shows (server `filterPms`).
    params: {
      ...(months.length ? { month: months } : {}),
      ...(opts?.allMonths ? { allMonths: 1 } : {}),
      ...(opts?.excludeTypes?.length ? { excludeType: opts.excludeTypes } : {}),
    },
    // Repeated keys (month=a&month=b), empties dropped — same scheme as fetchInventory.
    paramsSerializer: (p: Record<string, unknown>) => {
      const sp = new URLSearchParams();
      Object.entries(p).forEach(([key, val]) => {
        if (Array.isArray(val)) {
          val.forEach((item) => { if (item !== undefined && item !== null && item !== '') sp.append(key, String(item)); });
        } else if (val !== undefined && val !== null && val !== '') {
          sp.append(key, String(val));
        }
      });
      return sp.toString();
    },
  });
  return data;
}

/* [INV-PM-RANK 2026-09-21, user] The PM Recommendations ranking for every row of one inventory
   month — the PM Name dropdown on Inventory + My Inventory lists it in place of the A–Z roster.
   Mirrors server/src/services/pmRecommendations.ts InventoryPmRanking. */
export interface InventoryPmOption {
  pm: string;
  rank: number;
  /** The PM's campaigns and $ in the month being viewed, before this row's client. */
  count: number;
  usd: number;
  /** "new pair" — this row's rep has never had a client with this PM. */
  explore: boolean;
}
export interface InventoryPmRanking {
  month: string;
  /** Keyed by inventory row id; a row missing here keeps the plain list. */
  rows: Record<string, {
    rule: 'ROW' | 'RANKED';
    source: 'pm-recommendations' | 'inventory';
    candidates: InventoryPmOption[];
  }>;
}
export async function fetchInventoryPmRanking(month: string): Promise<InventoryPmRanking> {
  const { data } = await api.get<InventoryPmRanking>('/inventory/pm-ranking', { params: { month } });
  return data;
}

// One underlying row behind the My Inventory Refunds/Disputes or Disputes Won KPI
// (for its hover breakdown — website · person · amount). Mirrors the My Sales shape.
export interface InventoryRefundDispute {
  website: string;
  person: string;               // refund/dispute assignee_name ('' when unassigned)
  amount: number;
  type: 'Refund' | 'Dispute';
}

// KPI strip for the My Inventory section — all figures for one PM + month.
export interface InventoryKpis {
  placedInventory: number;
  placedInBank: number;
  currentRetentionPct: number;
  expectedRetentionPct: number;
  /* [UPSELL-HEALTH 2026-08-24] null = "HubSpot could not be read", NOT zero.
     These five are the only tiles that depend on HubSpot: the three upsell figures and the
     two totals that add the upsell in. The server used to send 0 on a failed fetch, which is
     indistinguishable from a real month with no upsells — on 2026-08-24 that showed One-Time
     Upsell $0.00 against a true $50,636.73, and dragged Expected EOM and Current Net In-Bank
     ~$50k light with it, with nothing on screen suggesting anything was wrong.
     `money()` renders null as an em-dash, so a missing figure now looks missing. */
  recurringUpsell: number | null;
  oneTimeUpsell: number | null;
  totalUpsell: number | null;
  refundsDisputes: number;
  disputesWon: number;
  expectedEOM: number | null;
  currentNetInBank: number | null;
  nextMonthInventory: number | null;
  // The individual rows behind refundsDisputes / disputesWon (same PM + month scope),
  // biggest first — drive the two KPI tiles' hover breakdown cards.
  refundsDisputesBreakdown: InventoryRefundDispute[];
  disputesWonBreakdown: InventoryRefundDispute[];
}

export async function fetchInventoryKpis(params: { pm?: string; month?: string } = {}): Promise<InventoryKpis> {
  const { data } = await api.get<InventoryKpis>('/inventory/kpis', { params });
  return data;
}

// Inventory View — per-PM inventory totals for the current month and next month.
// `month1`/`month2` are YYYY-MM strings (the rolling two-month window decided by
// the server), so the UI just renders whatever it's handed.
export interface InventoryViewRow {
  pm: string;
  month1Amount: number;
  month2Amount: number;
  total: number;
}

export interface InventoryViewSummary {
  month1: string; // YYYY-MM (current month)
  month2: string; // YYYY-MM (next month)
  rows: InventoryViewRow[];
  totals: { month1Amount: number; month2Amount: number; total: number };
}

export async function fetchInventoryViewSummary(): Promise<InventoryViewSummary> {
  const { data } = await api.get<InventoryViewSummary>('/inventory/view-summary');
  return data;
}

export async function updateInventoryRow(
  id: number,
  fields: Partial<InventoryRow>,
  opts?: { section?: string }
): Promise<InventoryRow> {
  // `section` tags which UI surface made the edit (e.g. "My Inventory") so the
  // audit log can attribute it correctly. Omitted → no header → logs as Inventory.
  const { data } = await api.put<InventoryRow>(
    `/inventory/${id}`,
    fields,
    opts?.section ? { headers: { 'X-App-Section': opts.section } } : undefined
  );
  return data;
}

// [BULK-EDIT 2026-08-03] Multi-row edit. One column, one value, many rows — applied
// SERVER-side in a couple of set-based statements. Never loop updateInventoryRow over a
// selection: the DB is remote (~269ms per round trip), so a few hundred rows that way is
// minutes of wall-clock and a pool full of writes.
export interface InventoryBulkSkip {
  id: number;
  website: string | null;
  reason: string;
}

export interface InventoryBulkResult {
  /** Rows actually written. */
  updated: number;
  /** Rows that already held the target value — no write, not in the undo batch. */
  unchanged: number;
  /** Rows deliberately left alone (out of scope, past month, column not editable). */
  skipped: InventoryBulkSkip[];
  /** Undo handle. null when nothing was written. */
  batchId: string | null;
}

export async function bulkUpdateInventory(
  ids: number[],
  field: string,
  value: any,
  opts?: { section?: string }
): Promise<InventoryBulkResult> {
  const { data } = await api.put<InventoryBulkResult>(
    '/inventory/bulk',
    { ids, field, value },
    opts?.section ? { headers: { 'X-App-Section': opts.section } } : undefined
  );
  return data;
}

export async function undoBulkInventoryUpdate(
  batchId: string,
  opts?: { section?: string }
): Promise<{ restored: number; skipped: number }> {
  const { data } = await api.post<{ restored: number; skipped: number }>(
    '/inventory/bulk/undo',
    { batchId },
    opts?.section ? { headers: { 'X-App-Section': opts.section } } : undefined
  );
  return data;
}

export async function createInventoryRow(row: Partial<InventoryRow>): Promise<InventoryRow> {
  const { data } = await api.post<InventoryRow>('/inventory', row);
  return data;
}

export async function deleteInventoryRow(id: number): Promise<void> {
  await api.delete(`/inventory/${id}`);
}

/** [INV-SPLIT 2026-10-07] Split one current-month website between PMs (admins). The amounts must add
 *  up to the row's amount. This month's payments for the website lose their retention, to be mapped
 *  again by PM. */
export async function splitInventoryRow(
  id: number,
  parts: { pm: string; amount: number }[],
): Promise<{ parts: InventoryRow[]; paymentsUnmapped: number }> {
  const { data } = await api.post<{ parts: InventoryRow[]; paymentsUnmapped: number }>(`/inventory/${id}/split`, { parts });
  return data;
}

/** [INV-SPLIT 2026-10-07] Merge a split website back into one row (part 1's PM keeps it). */
export async function unsplitInventoryRow(id: number): Promise<{ row: InventoryRow; removedIds: number[] }> {
  const { data } = await api.post<{ row: InventoryRow; removedIds: number[] }>(`/inventory/${id}/unsplit`);
  return data;
}

export async function deleteRefund(id: number): Promise<void> {
  // Soft delete — DB sets deleted_status='Deleted' so the row drops out of every
  // aggregation but stays accessible via the trash drawer.
  await api.delete(`/refunds/${id}`);
}

export async function deleteDispute(id: number): Promise<void> {
  await api.delete(`/disputes/${id}`);
}

// [PAYMENT-DELETE 2026-07-17] Soft-delete a payment (admin, from the Edit Data modal) —
// server sets excluded=1 + deleted_at so it drops out of the Payments table but stays in
// the Trash (bin) and can be restored. Never a hard delete (the sync would re-insert it).
export async function deletePayment(id: number): Promise<void> {
  await api.delete(`/payments/${id}`);
}

export async function restorePayment(id: number): Promise<void> {
  await api.put(`/payments/${id}/restore`);
}

// [CASE-ASSIGN 2026-08-13] `team` + `assignee_name` are sent TOGETHER when a
// dropdown pick changes both (picking a name sets its Team; switching Team clears a
// name that can't sit under it), so the pair is never half-written. `null` clears.
export async function updateRefundField(
  id: number,
  fields: { website?: string; email?: string; team?: string | null; assignee_name?: string | null },
): Promise<any> {
  const { data } = await api.put(`/refunds/${id}`, fields);
  return data;
}

export async function updateDisputeField(
  id: number,
  fields: { website?: string; email?: string; team?: string | null; assignee_name?: string | null },
): Promise<any> {
  const { data } = await api.put(`/disputes/${id}`, fields);
  return data;
}

export async function restoreRefund(id: number): Promise<void> {
  await api.put(`/refunds/${id}/restore`);
}

export async function restoreDispute(id: number): Promise<void> {
  await api.put(`/disputes/${id}/restore`);
}

// [ACCEPT-REFRESH 2026-09-17, user] The notification bell's Accept / Cancel. They were a bare
// fetch(), which skipped the adapter above — so the write never cleared the GET cache nor fired
// the on-write bus, and Refunds / Disputes / Application Logs kept serving their pre-Accept copy
// until a full reload. Through `api` they behave like every other save (e.g. updateRefundField).
// Both resolve only on a 2xx; a refusal throws with the server's `error` in err.response.data.
export async function acceptNotificationSuggestion(
  body: { id: number; type: 'refund' | 'dispute'; team: string; name: string },
): Promise<{ success: boolean; team: string; name: string }> {
  const { data } = await api.post('/notifications/accept', body);
  return data;
}

export async function cancelNotificationCard(body: { id: number; type: 'refund' | 'dispute' }): Promise<void> {
  await api.post('/notifications/cancel', body);
}

// Restore a trashed inventory row (id is the deleted_inventory archive_id).
export async function restoreInventory(archiveId: number): Promise<void> {
  await api.put(`/deleted/inventory/${archiveId}/restore`);
}

export interface DeletedItem {
  id: number;
  source: 'refund' | 'dispute' | 'inventory' | 'payment';
  website: string | null;
  email: string | null;
  client_name: string | null;
  amount: string | number;
  event_date: string | null;
  account_name: string | null;
  charge_id: string | null;
  dispute_status?: string | null;
  pm?: string | null;            // inventory only
  deleted_by?: string | null;    // inventory only
  month_str: string | null;
  deleted_at: string | null;
  note?: string | null;          // [DELETE-NOTE 2026-06-29] reason-for-deletion note
}

export async function fetchDeletedItems(): Promise<DeletedItem[]> {
  const { data } = await api.get<{ items: DeletedItem[] }>('/deleted');
  return data.items;
}

// [DELETE-NOTE 2026-06-29] Save (upsert) the reason-for-deletion note for a trash item.
// An empty string clears it. Returns the stored note (null when cleared).
export async function saveDeleteNote(
  source: DeletedItem['source'], itemId: number, note: string,
): Promise<string | null> {
  const { data } = await api.put<{ success: boolean; note: string | null }>(
    '/deleted/note', { source, itemId, note }
  );
  return data.note;
}

export async function fetchDeletedCount(): Promise<number> {
  const { data } = await api.get<{ total: number }>('/deleted/count');
  return data.total;
}

// ── App Alerts ───────────────────────────────────────────────────────────────
// [APP-ALERTS 2026-09-04] Faults the app has detected in itself — a feed that stopped, money
// counted twice, a website that can't match. NOT the same thing as the Google Chat alerts,
// which list work waiting to be done (unmapped payments, red inventory) and are unchanged.

export type AppAlertSeverity = 'high' | 'medium' | 'low';

export interface AppAlert {
  id: number;
  key: string;
  detector: string;
  severity: AppAlertSeverity;
  /** Short headline for the card. */
  title: string;
  /** Plain English: what happened. */
  summary: string;
  /** Plain English: what it breaks while it is true. */
  impact: string | null;
  /** In-app path to go and deal with it, when there is one. */
  linkPath: string | null;
  /** The deep payload — ids, amounts, timestamps, error text, what was already tried.
   *  Shown behind "Details"; this is what you read when you sit down to fix it. */
  context: Record<string, unknown>;
  status: 'open' | 'resolved';
  /** How many separate times this exact problem has occurred. >1 means it came back. */
  timesSeen: number;
  /** How the last occurrence ended — 'cleared' = the detector stopped seeing it. */
  resolution: string | null;
  /** What was actually done about it, written at the time of the fix. */
  fixNote: string | null;
  fixedBy: string | null;
  fixedAt: string | null;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  resolvedAt: string | null;
}

export interface AppAlertsResponse {
  open: AppAlert[];
  /** A bounded page of history — nothing is ever deleted, so this is not everything. */
  resolved: AppAlert[];
  /** How many resolved records exist in total, so the drawer can say what it is not showing. */
  resolvedTotal: number;
}

export async function fetchAppAlerts(historyLimit?: number): Promise<AppAlertsResponse> {
  const { data } = await api.get<AppAlertsResponse>(
    '/app-alerts', historyLimit ? { params: { historyLimit } } : undefined,
  );
  return { open: data.open ?? [], resolved: data.resolved ?? [], resolvedTotal: data.resolvedTotal ?? 0 };
}

export async function fetchAppAlertCount(): Promise<{ open: number; high: number }> {
  const { data } = await api.get<{ open: number; high: number }>('/app-alerts/count');
  return { open: data.open ?? 0, high: data.high ?? 0 };
}

/** [FREELANCER-REPLY 2026-10-23, user] The freelancer NAL / Duplicate card's two buttons. OK sends the reply into that email's
 *  conversation ('already' = a reply was in Gmail first, nothing sent); Cancel sends nothing and the email is not offered again.
 *  Both resolve only on a 2xx; a refusal throws with the server's `error` in err.response.data. */
export interface FreelancerReplyResult {
  dumpId: number;
  kind: 'NAL' | 'Duplicate';
  status: 'sent' | 'already' | 'ignored';
  to: string;
  subject: string;
  replyText: string;
  gmailId: string | null;
}
export async function sendFreelancerReply(dumpId: number): Promise<FreelancerReplyResult> {
  const { data } = await api.post<FreelancerReplyResult>(`/app-alerts/freelancer-reply/${dumpId}/send`);
  return data;
}
export async function ignoreFreelancerReply(dumpId: number): Promise<FreelancerReplyResult> {
  const { data } = await api.post<FreelancerReplyResult>(`/app-alerts/freelancer-reply/${dumpId}/ignore`);
  return data;
}

/** [LIVE-CONNECTIONS 2026-09-30] One live reading for the card at the top of App Alerts.
 *  `peak` and `refused` count from `startedAt` (the database's last restart). */
export interface DbConnections {
  inUse: number;
  max: number;
  peak: number;
  refused: number;
  startedAt: string;
  at: string;
}

export async function fetchDbConnections(): Promise<DbConnections> {
  // Uncached (see isCacheable), so no default time limit applies — keep a short one of its own.
  const { data } = await api.get<DbConnections>('/app-alerts/db-connections', { timeout: 8000 });
  return data;
}

/** [LEAD-LIVE-CARD 2026-10-21] The live "Lead assignment" card in App Alerts: TODAY's Data Status Fetch (Processed of today's Leads
 *  Data rows) and Assign to HubSpot (Assigned of today's Ready rows, ROW / US) + the two automatic jobs' state (server services/
 *  appAlerts.ts readLeadAssignmentLive). */
export interface LeadAssignmentLive {
  day: string;
  at: string;
  fetch: {
    on: boolean; total: number; processed: number; waiting: number; noDetails: number;
    lastCheckAt: string | null; checking: boolean; waitedFor: string | null;
    failing: boolean; stale: boolean; error: string | null;
    /** [AUTO-STATUS-HOURS 2026-10-21] the checks' hours (12:30–6:30 PM IST): open = checking now, tomorrow = today's are over
     *  (optional: a server from before it sends none) */
    hours?: { from: string; to: string; open: boolean; tomorrow: boolean };
  };
  assign: {
    on: boolean; ready: number; assigned: number;
    row: { ready: number; assigned: number }; us: { ready: number; assigned: number };
    held: number; noWebsite: number; noEmail: number;
    running: { part: string; phase: string; done: number; total: number; startedAt: string } | null;
    lastRunAt: string | null; lastAssigned: number | null;
    window: { part: string; from: string; to: string } | null;
    next: { part: string; from: string; to: string; tomorrow: boolean };
    paused: boolean; failing: boolean; error: string | null;
  };
}

export async function fetchLeadAssignmentLive(): Promise<LeadAssignmentLive> {
  // Uncached (see isCacheable), polled like the database card — a short time limit of its own.
  const { data } = await api.get<LeadAssignmentLive>('/app-alerts/lead-assignment', { timeout: 8000 });
  return data;
}

// Refunds API

interface FetchRefundsParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  account?: string;
  month?: string;
  /** [MULTI-MONTH 2026-07-15] Multi-select months. Serialised to a CSV `months=`. */
  months?: string[];
  search?: string;
}

export async function fetchRefunds(params: FetchRefundsParams = {}): Promise<RefundsResponse> {
  const { data } = await api.get<RefundsResponse>('/refunds', { params: monthsParams(params) });
  return data;
}

// Stats API — per-account monthly reconciliation table (computed from the DB,
// reproducing the sheet's "Stats" tab; see server routes/stats.ts).
export interface StatsRow {
  account_name: string;
  payment: number;
  refund: number;
  dispute: number;
  dispute_won: number;
  in_bank: number;
  true_refund: number;
  false_refund: number;
  balance_after_true: number;
  balance_after_false: number;
}

export interface StatsResponse {
  month: string;
  rows: StatsRow[];
  total: StatsRow;
}

export async function fetchStatsMonths(): Promise<string[]> {
  const { data } = await api.get<{ months: string[] }>('/stats/months');
  return data.months;
}

export async function fetchStats(month: string): Promise<StatsResponse> {
  const { data } = await api.get<StatsResponse>('/stats', { params: { month } });
  return data;
}

/* [PAYOUT-DETAILS 2026-08-09] Stripe payout balances behind the Stats tab's second panel. */

export interface PayoutRow {
  account_name: string;
  next_payout_date: string | null;
  available_balance: number;
  pending_balance: number;
  total_payout_amount: number;
  status: 'success' | 'failed' | 'missing_key';
}

export interface PayoutSnapshot {
  rows: PayoutRow[];
  total: { available_balance: number; pending_balance: number; total_payout_amount: number };
  last_updated: string | null;
}

/** The last stored snapshot — instant, never calls Stripe. */
export async function fetchPayouts(): Promise<PayoutSnapshot> {
  const { data } = await api.get<PayoutSnapshot>('/stats/payouts');
  return data;
}

/**
 * Pull every account from Stripe and store the result. Slow by nature (two API calls per account),
 * so the caller shows skeletons for the duration. A longer timeout than the default: 14 accounts
 * against a slow Stripe is well past a normal request.
 */
export async function refreshPayouts(): Promise<PayoutSnapshot> {
  const { data } = await api.post<PayoutSnapshot>('/stats/payouts/refresh', null, { timeout: 120000 });
  return data;
}

export async function fetchRefundFilters(month?: string): Promise<RefundFiltersResponse> {
  const { data } = await api.get<RefundFiltersResponse>('/refunds/filters', {
    params: month ? { month } : {},
  });
  return data;
}

// Disputes API

interface FetchDisputesParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  account?: string;
  month?: string;
  /** [MULTI-MONTH 2026-07-15] Multi-select months. Serialised to a CSV `months=`. */
  months?: string[];
  search?: string;
}

export async function fetchDisputes(params: FetchDisputesParams = {}): Promise<DisputesResponse> {
  const { data } = await api.get<DisputesResponse>('/disputes', { params: monthsParams(params) });
  return data;
}

export async function fetchDisputeFilters(month?: string): Promise<DisputeFiltersResponse> {
  const { data } = await api.get<DisputeFiltersResponse>('/disputes/filters', {
    params: month ? { month } : {},
  });
  return data;
}

// HubSpot API

export interface HubSpotOwner {
  id: string;
  name: string;
  email: string;
  role: 'sales' | 'pm' | 'both' | null;
  teams: string[];
  /** [AMAAN-ROW 2026-09-25] Sells for both books — the deal popup asks "ROW or US?" for his New Sale deals. */
  rowOptional?: boolean;
}

export interface HubSpotDeal {
  dealName: string;
  pipeline: string;
  ownerName: string;
  paymentType: string;
  closeDate: string;
  companyUrl: string;
  email: string;
  ranking: number;
  performance: number;
  tech: number;
  creative: number;
  existingDealId?: string;
  // [DEAL-SPLIT 2026-06-29] Which deal-type slot (suffix: r_new_sale / o_new_sale /
  // r_so / o_so) this deal belongs to, and its part within that slot (0, or 0/1 when
  // the slot is split into two deals). The server groups deals by slot+part to (a)
  // back-fill the existing deal id idempotently and (b) persist the deal_splits layout.
  // [PIA-DEAL 2026-09-09] 'pia_amount' is now a fifth slot; it is never split, so its
  // part is always 0.
  slot?: string;
  part?: number;
  // [SECOND-VOICE 2026-08-04] Second sales rep credited on the deal, or the literal
  // 'Self Closed'. Sent for SALES deals only (r_new_sale / o_new_sale); undefined on PM
  // upsell deals, and the server then omits the HubSpot property entirely.
  // [PIA-DEAL 2026-09-09] Also sent on PIA deals, in either pipeline — optional there.
  secondVoice?: string;
  // [PIA-DEAL 2026-09-09] PIA deals only: the term and the month it runs through, already
  // formatted for HubSpot's `PIA Month` text property — e.g. "5 (till December 2026)".
  // Undefined on sale/upsell deals, and the server then omits the property entirely.
  piaMonth?: string;
}

export async function fetchHubSpotOwners(): Promise<HubSpotOwner[]> {
  const { data } = await api.get<HubSpotOwner[]>('/hubspot/owners');
  return data;
}

/** [AMAAN-ROW 2026-09-25] Present when a both-books rep (Amaan Sofi) owns a saved New Sale deal on a
 *  current-month payment: the deal popup must then ask "ROW side or US side?". */
export interface RowChoiceAsk { needed: boolean; current?: 'ROW' | 'US'; owner?: string }

export async function createHubSpotDeals(deals: HubSpotDeal[], paymentId?: number, rowChoice?: 'ROW' | 'US'): Promise<{ results: any[]; dealIds: string[]; dealOwner?: string; dealSplits?: string | null; rowChoice?: RowChoiceAsk; rowStatus?: 'ROW' | null }> {
  // [DEAL-DEDUP 2026-06-25] Bounded timeout — the shared client has none, so a stalled
  // deal request used to hang indefinitely; the user would give up and re-submit, which
  // (before the server-side idempotency guard) created a duplicate deal. With the guard,
  // a re-submit now UPDATES the same deal, so this timeout just gives a clean, fast error.
  // [DEAL-SPLIT 2026-06-29] dealSplits = the JSON split layout the server persisted, so
  // the caller can stamp it onto the row (and the next open re-uses the same deal ids).
  // [AMAAN-ROW 2026-09-25] rowChoice = the popup's "ROW or US?" answer, stamped by the server in this
  // same request; rowStatus (reply) = the flag it stored, undefined when no answer was applied.
  const { data } = await api.post<{ results: any[]; dealIds: string[]; dealOwner?: string; dealSplits?: string | null; rowChoice?: RowChoiceAsk; rowStatus?: 'ROW' | null }>(
    '/hubspot/deals', rowChoice ? { deals, paymentId, rowChoice } : { deals, paymentId }, { timeout: 90000 }
  );
  return data;
}

/** [AMAAN-ROW 2026-09-25, user] Save the "ROW side or US side?" answer for a payment. The server checks
 *  every rule (current month, a both-books rep on a New Sale deal); returns the stored row_status. */
export async function setPaymentRowChoice(paymentId: number, choice: 'ROW' | 'US'): Promise<{ ok: boolean; rowStatus: 'ROW' | null }> {
  // Bounded, like the deal save: a stalled request must never leave the popup stuck on "Saving…".
  const { data } = await api.post<{ ok: boolean; rowStatus: 'ROW' | null }>('/hubspot/row-choice', { paymentId, choice }, { timeout: 30000 });
  return data;
}

// [DEAL-CONFIRM 2026-06-23] Current state of existing deals, for the deal-confirm
// popup's "old → new" diff. Best-effort — a missing/archived deal is just absent
// from the result; the caller falls back to showing new-only for it.
export interface ExistingHubSpotDeal {
  dealId: string;
  dealName: string;
  pipeline: string;
  paymentType: string;
  amount: number | null;
  ownerName: string;
  // [SECOND-VOICE 2026-08-04] Current second_voice stored on the deal, echoed back so the
  // popup can PRE-FILL it on update instead of showing a blank the user might overwrite.
  secondVoice: string;
}

// [DEAL-OWNER 2026-07-28] `paymentId` is optional and purely a self-heal hint: the
// server persists the owner it just read onto that payment when the row has a deal
// link but a blank deal_owner (deals created before the owner was stamped). Costs no
// extra request — it rides on the read the deal-confirm popup already makes.
export async function fetchHubSpotDeals(dealIds: string[], paymentId?: number): Promise<ExistingHubSpotDeal[]> {
  if (!dealIds.length) return [];
  const { data } = await api.post<ExistingHubSpotDeal[]>('/hubspot/deals/read', { dealIds, paymentId });
  return data;
}

// EOM / Dashboard API

export interface DeptRow { ranking: number; creative: number; tech: number; performance: number; }

/** [ROW DASHBOARD 2026-09-03] The ROW share of each EOM bucket. USA = whole month - this. */
export interface EomRowDetail {
  salesRecurring: number;
  salesOneTime: number;
  salesPia: number;
  pmRetention: number;
  pmRecurringUpsell: number;
  pmOneTimeUpsell: number;
  pmPia: number;
  pmInventory: number;
  provInventory: number;
  /** ROW's own money out / money kept, split the way the summary table shows it. Always 0 so
   *  far — there has never been a ROW refund or dispute — but carried so the table needs no new
   *  plumbing the day the first one appears. */
  salesRefundDispute: number;
  salesDisputeWon: number;
  pmRefundDispute: number;
  pmDisputeWon: number;
}

export interface EOMSummary {
  eom: {
    recurring_sales: DeptRow;
    one_time_sales: DeptRow;
    sales_pia: DeptRow;
    sales_refund_dispute: DeptRow;
    sales_disputes_won: DeptRow;
    pm_inventory: DeptRow;
    pm_retention_achieved: DeptRow;
    pm_recurring_upsell: DeptRow;
    pm_one_time_upsell: DeptRow;
    pm_pia: DeptRow;
    pm_refunds_disputes: DeptRow;
    pm_disputes_won: DeptRow;
    /** [SHOPIFY 2026-10-19] The month's Shopify payouts, in the ranking slot. Optional: an older payload has none. */
    shopify?: DeptRow;
  };
  deptRevenue: { team: string; gross: number; refundDispute: number; disputeWon: number }[];
  /** [ROW DASHBOARD 2026-09-03] Optional: absent on an older payload, which makes the USA
   *  board the whole month and the ROW board empty — the pre-split behaviour. */
  rowDetail?: EomRowDetail;
  provInventory: number;
  retentionPeriods: { label: string; expected: number; achieved: number }[];
}

export async function fetchEOMSummary(month: string): Promise<EOMSummary> {
  const { data } = await api.get<EOMSummary>('/eom/summary', { params: { month } });
  return data;
}

export interface EOMadjustments {
  sales: DeptRow;
  pm: DeptRow;
}

export async function fetchEOMadjustments(month: string): Promise<EOMadjustments> {
  const { data } = await api.get<EOMadjustments>('/eom/adjustments', { params: { month } });
  return data;
}

export async function saveEOMadjustment(month: string, type: 'sales' | 'pm', values: DeptRow): Promise<void> {
  await api.put('/eom/adjustments', { month, type, ...values });
}

/* ── Process Analysis ───────────────────────────────────────────────────────
   One GET returns the whole tab. The shapes mirror
   server/src/services/processAnalysis.ts exactly — see that file for what each
   figure MEANS (in particular which of the three "In Bank" definitions applies
   where, and why the industry/geography blocks carry a coverage object). */

/** How far the in-progress month has run. `day` is inclusive: day 3 of 31. */
export interface PaMonthProgress { month: string; day: number; daysInMonth: number }

export interface PaWindow {
  start: string;
  end: string;
  months: string[];
  /** The trailing month if it has not finished yet — null when every month in the window has. */
  inProgress: PaMonthProgress | null;
}

/**
 * Every month's value for one headline KPI, plus the month that has not finished yet.
 *
 * [MONTH-BOUNDARY / MONTH-FILTER 2026-08-03] The server ships no precomputed "current month"
 * figure: the headline is month-selectable, so the comparison must be derivable for ANY month
 * in the window. `kpiForMonth()` in ProcessAnalysisPage owns that arithmetic, and owns it
 * alone — a second copy on the server would be free to drift from this one. The running month
 * is deliberately kept OUT of `series` so it can never be averaged in with whole months.
 */
export interface PaKpiCard {
  series: { month: string; value: number }[];
  mtd: (PaMonthProgress & { value: number }) | null;
}

/**
 * [NET SALES IN BANK 2026-09-17] The New Sales tab's boxes for a month, BEFORE refunds (was the
 * Payments tab's Bank Summary new-sale columns). `total` = recurring + oneTime + salesPia + recovery.
 */
export interface PaTrendPoint {
  month: string;
  /** New Sales "Recurring New Sale" (Team ROW - Sales included). */
  recurring: number;
  /** New Sales "One-Time New Sale". */
  oneTime: number;
  /** New Sales "Sales PIA". */
  salesPia: number;
  /** Sales share of recovery (inside Total New Sales). */
  recovery: number;
  /** New Sales "Total New Sales". */
  total: number;
}

/** My Inventory's Performance Snapshot per month, unfiltered by PM. */
export interface PaInventoryTrendPoint {
  month: string;
  /** "Placed Inventory". */
  placedInventory: number;
  /** "Placed in Bank". */
  placedInBank: number;
  /** "Current Retention" — placedInBank ÷ placedInventory. */
  retentionPct: number;
}

/**
 * [REVENUE MIX 2026-08-17] A month's collected revenue split into buckets that are disjoint BY
 * COLUMN on `payments` — the retention columns, the new-sale columns, the upsell/SO columns and
 * the PIA columns — so `total` is a plain sum and double-counts nothing.
 */
export interface PaRevenueMixPoint {
  month: string;
  /** The four dept retention buckets — money kept from clients we already had. */
  retention: number;
  /** Recurring + one-time new sale. */
  newSales: number;
  /** Recurring + one-time upsell / second order. */
  upsell: number;
  /** [PIA IN GROSS 2026-09-01] The four dept PIA buckets — money paid in advance. */
  pia: number;
  /** [SHOPIFY 2026-10-19] Shopify payouts (inside total). Optional: an older server payload has none. */
  shopify?: number;
  /** retention + newSales + upsell + pia + shopify. */
  total: number;
}

/**
 * How much of a block's revenue could actually be attributed to the attribute it charts.
 * Industry and state come from the enrichment store and are NOT known for every client, so
 * every industry/geography block ships this and the UI prints it — a top-five states chart
 * covering 28% of revenue must never read as if it covered all of it.
 */
export interface PaCoverage {
  attributed: number;
  total: number;
  pct: number;
  clients: number;
  clientsTotal: number;
}

export interface PaIndustryRow { industry: string; clients: number; inBank: number; atv: number }
export interface PaConversionRow { industry: string; leads: number; sales: number; conversionPct: number }
export interface PaStateRow {
  state: string;
  clients: number;
  inBank: number;
  industries: { industry: string; clients: number; inBank: number }[];
}
export interface PaRepCohortRow {
  rep: string; wonClients: number; m1: number; m2: number; m1Pct: number; m2Pct: number;
}
export interface PaRepRankRow {
  rep: string; inBank: number; sales: number; leads: number; conversionPct: number; atv: number;
}
export interface PaPmTenureRow {
  pm: string; clients: number; avgMonths: number; maxMonths: number; retentionPct: number;
}
export interface PaPmQuadrantRow {
  pm: string; retentionPct: number; upsell: number; inventory: number; placed: number;
  quadrant: 'Both strong' | 'Retention only' | 'Upsell only' | 'Both weak';
}
export interface PaRevenueRetentionPoint {
  month: string;
  /** What last month's paying clients were worth LAST month. */
  base: number;
  /** What those SAME clients were worth this month. */
  kept: number;
  /** kept ÷ base. Above 100 means the existing book grew with no new clients. */
  nrrPct: number;
  expansion: number;
  /** Reported positive. */
  contraction: number;
}
export interface PaRefundRatePoint {
  month: string;
  gross: number;
  /** The Payments tab's "Past Refund/Disputes" cell. */
  refundsDisputes: number;
  /** The Payments tab's "Dispute Won/Recovery" cell — won disputes plus recovery. */
  disputesWonRecovery: number;
  ratePct: number;
}
/** [MONEY FLOW 2026-08-27] One month, gross to net, the way the EOM tab reads it. */
export interface PaMoneyFlowPoint {
  month: string;
  retention: number;
  upsell: number;
  newSales: number;
  /** [PIA IN GROSS 2026-09-01] Money paid in advance — the fourth bucket EOM carries in Gross. */
  pia: number;
  /** [SHOPIFY 2026-10-19] Shopify payouts — the EOM tab's Shopify line, inside Gross. Optional on an older payload. */
  shopify?: number;
  /** retention + upsell + newSales + pia + shopify. */
  gross: number;
  /** Refunds + lost disputes, reported positive. */
  refundsDisputes: number;
  /** Won disputes + recovery — money kept back. */
  disputesWonRecovery: number;
  /** gross − refundsDisputes + disputesWonRecovery — the EOM tab's Net formula. */
  net: number;
}
/** [MONEY COMPARE 2026-08-31] Same shape; pre-2026 months carry the deal-sourced flag. */
export interface PaMoneyComparePoint extends PaMoneyFlowPoint {
  dealSourced?: boolean;
}
export interface PaRetentionLeak {
  month: string;
  somInventory: number; exclusions: number; initialExpected: number;
  retained: number; cancelled: number; atRisk: number; pending: number;
}

export interface PaFunnelStep {
  label: string;
  value: number;
  /** Percent of the PREVIOUS stage that reached this one. null on the first stage. */
  pctOfPrev: number | null;
}
/** One month of the funnel. ⚠ Only months with activity data appear — the table starts 2026-03. */
export interface PaSalesFunnelMonth {
  month: string;
  steps: PaFunnelStep[];
  calls: number;
  talkHours: number;
  noShows: number;
}

export interface PaClientBaseClient {
  /** TRIM(website) exactly as the Inventory tab shows it. */
  website: string;
  status: string | null;
  /** Recurring / One-Time — both count, so this explains a month's split. */
  type: string | null;
  /** [SECOND-MONTH 2026-08-27] On LOST entries: the row was also NEW this month. */
  sameMonth?: boolean;
}
export interface PaClientBasePmRow {
  pm: string;
  /** Rows whose Campaign Status is `New Campaign` that month. */
  gained: number;
  /** Rows marked `Cancelled` that month. */
  lost: number;
  /** [SECOND-MONTH 2026-08-27] Of `lost`, rows also NEW this month. Optional: stale payloads. */
  lostSameMonth?: number;
  gainedClients: PaClientBaseClient[];
  lostClients: PaClientBaseClient[];
}
export interface PaClientBasePoint {
  month: string; active: number; gained: number; lost: number; net: number;
  /** [SECOND-MONTH 2026-08-27] Of `lost`, rows also NEW this month — the red bar's division. */
  lostSameMonth?: number;
  /** Per-PM split behind the bars. Sorted by lost desc, then gained desc, then name. */
  byPm: PaClientBasePmRow[];
}

export interface ProcessAnalysisData {
  window: PaWindow;
  generatedAt: string;
  thresholds: {
    minLeadsForConversion: number;
    minClientsForAtv: number;
    minClientsForCohort: number;
    minClientsForTenure: number;
  };
  /* [EOM KPI 2026-09-01, user] `eom` is the month's net from the same series the Revenue engine
     chart draws — the EOM tab's Net SEO Total. Optional so a server that predates it still
     renders the other three rather than throwing. */
  kpis: { dailyLeads: PaKpiCard; salesInBank: PaKpiCard; pmInBank: PaKpiCard; eom?: PaKpiCard };
  /* [UPSELL-HEALTH 2026-08-24] Months whose HubSpot upsell could not be read; PM in bank is
     understated for those. Optional so an older server payload reads as healthy. */
  upsellFailedMonths?: string[];
  salesTrend: PaTrendPoint[];
  inventoryTrend: PaInventoryTrendPoint[];
  revenueMix: PaRevenueMixPoint[];
  industry: {
    coverage: PaCoverage;
    byInBank: PaIndustryRow[];
    byConversion: PaConversionRow[];
    byAtv: PaIndustryRow[];
  };
  geography: { coverage: PaCoverage; states: PaStateRow[] };
  salesReps: {
    cohortMonths: string[];
    cohort: PaRepCohortRow[];
    scoreboardWindow: string[];
    /** Whole roster ranked by in-bank, split down the middle. Both halves are high-to-low. */
    topHalf: PaRepRankRow[];
    bottomHalf: PaRepRankRow[];
  };
  pms: {
    /** [FULL-TIMELINE 2026-08-27] First month this section covers. Optional: stale payloads. */
    historyFrom?: string;
    tenure: PaPmTenureRow[];
    quadrant: PaPmQuadrantRow[];
    medianRetentionPct: number;
    medianUpsell: number;
  };
  clientBase: PaClientBasePoint[];
  /** One entry per month that HAS activity data. Empty when the table is unreachable. */
  funnel: PaSalesFunnelMonth[];
  /** COMPLETE months only — a part month cannot be compared against a full one. */
  revenueRetention: PaRevenueRetentionPoint[];
  refundRate: PaRefundRatePoint[];
  /** [MONEY FLOW 2026-08-27] Optional so a payload from a server that predates it still renders. */
  moneyFlow?: PaMoneyFlowPoint[];
  /** [MONEY COMPARE 2026-08-31] Full-history monthly series for the year/quarter/month
   *  comparisons. Optional: a stale payload simply renders no comparison block. */
  moneyCompare?: { historyFrom: string; points: PaMoneyComparePoint[] };
  /** One entry per COMPLETE month. The running month is deliberately absent. */
  retentionLeak: PaRetentionLeak[];
}

export async function fetchProcessAnalysis(): Promise<ProcessAnalysisData> {
  const { data } = await api.get<ProcessAnalysisData>('/process-analysis');
  return data;
}

/* ── [PM-RECS 2026-08-25] PM Recommendations ───────────────────────────────────────────────────
   Mirrors server/src/services/pmRecommendations.ts exactly — see that file for what every field
   means and how it is computed. Rates are 0..1. [PERF 2026-08-25] The payload carries only what
   the page renders; the scoring internals stay server-side. */

/** [PM-RECS v3 2026-08-26] Input 1 — the PM's own retention over the last six complete months. */
export interface RecRecord {
  months: number;
  clients: number;
  placedUsd: number;
  retainedUsd: number;
  retPct: number | null;
  shrunk: number;
}

/** [PM-RECS v3] Input 2 — the connection with this row's rep, as the score used it. */
export interface RecConnection {
  basis: 'rep' | 'overall' | 'none';
  liftPts: number;
}

/** [REP-RISK 2026-08-27] How this row's rep's own clients have fared, whoever managed them. */
export interface RecRepRisk {
  clients: number;
  survivalPct: number;
  poolPct: number;
}

/** [PM-RECS v3] Input 3 — the book in the month being assigned into, judged BEFORE the sale. */
export interface RecCapacity {
  count: number;
  usd: number;
  projectedCount: number;
  projectedUsd: number;
  withCount: number;
  withUsd: number;
  avgCount: number;
  avgUsd: number;
  index: number;
  verdict: 'room' | 'average' | 'above';
}

export interface RecLayer {
  clients: number;
  months: number;
  retentionRate: number | null;
  expectedRetentionRate: number | null;
  liftPts: number | null;
}

export interface RecClient {
  site: string;
  saleMonth: string;
  initialUsd: number;
  monthsOnBook: number;
  monthsHeld: number;
  keptUsd: number;
  expectedUsd: number;
  firstPm: string | null;
  lastPm: string | null;
  lastMonth: string;
  lastStatus: string | null;
  reassigned: boolean;
}

export interface RecCandidate {
  pm: string;
  team: string | null;
  rank: number | null;
  insufficient: boolean;
  confidence: 'High' | 'Medium' | 'Low' | 'None';
  record: RecRecord;
  connection: RecConnection;
  capacity: RecCapacity;
  why: 'record' | 'capacity' | 'fit' | 'explore' | 'row' | 'only';
  explore: boolean;
  overall: RecLayer;
  pair: RecLayer & { clients_: RecClient[] };
}

export interface RecCohortRow {
  site: string;
  rep: string | null;
  repActive: boolean;
  saleUsd: number;
  initialUsd: number;
  repRisk: RecRepRisk | null;
  isRow: boolean;
  onInventory: boolean;
  originalPm: string | null;
  assignedPm: string | null;
  assignedIsCurrent: boolean;
  nowMonth: string | null;
  nowStatus: string | null;
  nowAmount: number | null;
  reassigned: boolean;
  rule: 'ROW' | 'RANKED';
  candidates: RecCandidate[];
  assignedRank: number | null;
  projectedBefore: number;
}

export interface PmRecommendationsData {
  month: string;
  availableMonths: string[];
  generatedAt: string;
  window: { firstInventoryMonth: string; lastObservableMonth: string; handovers: number; handoversWithRep: number; repFromPayments: number; repFromDeals: number; dealsMirrorAvailable: boolean };
  rowPm: string | null;
  roster: { pms: { name: string; team: string | null }[]; activeReps: string[]; rowReps: string[] };
  cohort: RecCohortRow[];
  notOnInventory: { site: string; rep: string | null; saleUsd: number }[];
  capacity: { month: string; avgCount: number; avgUsd: number; poolSize: number };
  params: {
    horizon: number; minPmMonths: number;
    record: { from: string; to: string; months: number; k: number; poolScore: number; prior: number };
    weights: { connection: number; room: number; explore: number; liftCap: number; pairK: number };
  };
}

/** `month` omitted → the server's default (the last complete month). */
export async function fetchPmRecommendations(month?: string): Promise<PmRecommendationsData> {
  const { data } = await api.get<PmRecommendationsData>('/pm-recommendations', month ? { params: { month } } : undefined);
  return data;
}

/* ── [MY-PERF 2026-09-02] My Performance — mirrors server/services/myPerformance.ts ──────── */
export interface MyPerfClientMove { website: string; amountUsd: number; wholeCompany: boolean }
export interface MyPerfMonth {
  month: string; onBook: boolean; clients: number; placedUsd: number; retainedUsd: number;
  retentionPct: number | null; avgRetentionPct: number | null; upsellRecurringUsd: number;
  restPlacedUsd: number; restUpsellUsd: number; companyUpsellUsd: number; upsellOneTimeUsd: number; upsellDeals: number;
  refundsUsd: number; refundsCount: number; disputesLostUsd: number; disputesLostCount: number; disputesWonUsd: number;
  currentNetInBankUsd: number;
  bankedPer100: number | null; cancelledClients: number; cancelledUsd: number;
  arrived: MyPerfClientMove[]; left: MyPerfClientMove[];
}
export interface MyPerfSummary {
  firstMonth: string | null; lastMonth: string | null; monthsInRange: number; monthsOnBook: number;
  now: { month: string; clients: number; placedUsd: number; retainedUsd: number; retentionPct: number | null; upsellUsd: number; moneyOutUsd: number } | null;
  lifetime: {
    placedUsd: number; retainedUsd: number; retentionPct: number | null;
    upsellRecurringUsd: number; upsellOneTimeUsd: number; upsellDeals: number;
    refundsUsd: number; disputesLostUsd: number; disputesWonUsd: number;
    clientsArrived: number; clientsLeft: number; currentNetInBankUsd: number; bankedPer100: number | null;
    upsellTop5Pct: number | null; upsellDistinctClients: number;
  };
  bestMonth: { month: string; retentionPct: number } | null;
  weakestMonth: { month: string; retentionPct: number } | null;
  biggestBook: { month: string; placedUsd: number } | null;
  biggestUpsellMonth: { month: string; usd: number } | null;
}
export interface MyPerfProvenance { metric: string; plain: string; formula: string; tiesTo: string }
export interface MyPerfData {
  pm: string | null;
  roster: string[];
  canPickOthers: boolean;
  reason?: 'not_a_current_pm';
  range?: { from: string; to: string; floor: string };
  months?: MyPerfMonth[];
  summary?: MyPerfSummary;
  provenance?: MyPerfProvenance[];
  notes?: string[];
  generatedAt?: string;
}
export async function fetchMyPerformance(pm?: string): Promise<MyPerfData> {
  const { data } = await api.get<MyPerfData>('/my-performance', pm ? { params: { pm } } : undefined);
  return data;
}

/* ── Campaign History ────────────────────────────────────────────────────────
   [CAMPAIGN-HISTORY 2026-09-04] One website's whole life. Served by
   server/services/campaignHistory.ts; the client only formats. */
export interface CampaignMonth {
  month: string; pm: string | null;
  /** [CURRENT-AM 2026-09-10, user] The month's AM Names cell as stored ("A, B"), null when blank. */
  am: string | null;
  type: string | null; status: string | null;
  placed: number; received: number; upsell: number; refundsDisputes: number; discount: number;
  viaEmail: boolean; website: string; complete: boolean;
}
export interface CampaignDeal {
  closed: string | null; created: string | null; name: string | null;
  owner: string | null; amount: number; paymentType: string | null;
}
export interface CampaignPmStint {
  pm: string; from: string; to: string; months: number;
  placed: number; received: number; upsell: number;
  keptPct: number | null; endedCancelled: boolean;
}
export interface CampaignGap { from: string; to: string; months: number }
/* [ACTIVITIES 2026-09-04] One thing that happened to the campaign. Built server-side and already
   sorted newest first, ending at the campaign's earliest recorded step. */
export type CampaignActivityKind =
  | 'started' | 'joined' | 'returned' | 'pm_assigned' | 'pm_changed' | 'sold'
  | 'paid' | 'pm_month' | 'not_paid' | 'upsell' | 'discount'
  | 'price_change' | 'type_change' | 'moved'
  | 'refund' | 'dispute_open' | 'dispute_lost' | 'dispute_won'
  | 'cancelled' | 'left';
export interface CampaignActivity {
  date: string; month: string; kind: CampaignActivityKind;
  /** [TIMELINE 2026-09-10] true = `date` is a real day; absent = a month-level fact (show the month). */
  dated?: boolean;
  title: string; detail?: string; amount?: number;
  stats?: { label: string; value: string }[];
  major: boolean;
  tone: 'good' | 'bad' | 'warn' | 'flat';
}
export interface CampaignHistoryData {
  website: string;
  /** websites is always a single entry — Campaign History matches on the website alone.
   *  emails are for display only. */
  identity: { websites: string[]; emails: string[] };
  company: Record<string, any> | null;
  lead: Record<string, any> | null;
  /** [SALES OWNER 2026-09-07] `name` = who made the LATEST sale (comma-separated when several);
   *  `original` = who made the FIRST one, null when it is the same person or unknown. */
  salesOwner: {
    name: string;
    source: 'deal' | 'payment' | 'new_sale' | 'lead';
    when: string | null;
    original: { name: string; source: 'deal' | 'payment' | 'new_sale'; when: string } | null;
  } | null;
  /** [ABOUT 2026-09-04] Five identity facts for the left card, from the payments table. */
  about: {
    contacts: string[];
    lastPayment: { date: string; amount: number } | null;
    billedVia: string | null;
    billingDay: number | null;
    currentRun: { since: string; months: number; out: boolean } | null;
  };
  months: CampaignMonth[];
  activities: CampaignActivity[];
  gaps: CampaignGap[];
  pmStints: CampaignPmStint[];
  sales: CampaignDeal[];
  upsells: CampaignDeal[];
  /** [LIFETIME 2026-09-10] 'YYYY-MM' the server treats as this month; later months are Upcoming. */
  currentMonth: string;
  totals: {
    /** [LIFETIME 2026-09-10, user] Overall for the website: every month that has started. */
    placed: number;
    /** Retention over the same months — what Kept % divides. */
    received: number;
    /** ALL money ever received on the website; what the feed's payment lines add up to. */
    gross: number;
    upsell: number; refundsDisputes: number; discount: number;
    keptPct: number | null; monthsWithUs: number; firstMonth: string | null;
    firstPaymentMonth: string | null;
    lastMonth: string | null; monthsExcluded: string[];
  };
}
export async function fetchCampaignHistory(website: string): Promise<CampaignHistoryData> {
  const { data } = await api.get<CampaignHistoryData>('/campaign-history', { params: { website } });
  return data;
}

/* [CLIENT-PULSE 2026-09-09, user] Campaign History → "Client Pulse Details" tab.
   Everything here comes from ClientPulse's integration API: the scores, and a short-lived URL for
   their own timeline iframe. Fetched in the background once the campaign itself has loaded — it
   leaves our network, so it must not delay the main page.
   [CP-ROUTES 2026-09-10] Two calls now, not one, so a slow timeline never holds the scores back. */
export interface CampaignPulseScores {
  domain: string;
  /** false = archived on ClientPulse's side. Arrives as HTTP 200 with full scores, NOT a 404. */
  active: boolean;
  pm: string | null;
  monthlyAmount: number | null;
  health: { score: number | null; zone: 'green' | 'amber' | 'red' | null };
  risk: { score: number | null; zone: 'LOW' | 'ELEVATED' | 'HIGH' | null };
  /** null = ClientPulse has too little data to score it — render "N/A", never 0. */
  kpis: {
    communicationPulse: number | null;
    clientMood: number | null;
    responseTime: number | null;
    meetingHealth: number | null;
  };
  asOf: { snapshotDate: string | null; computedAt: string | null };
}
export type CampaignPulse =
  | { status: 'ok'; scores: CampaignPulseScores; fetchedAt: string }
  | { status: 'no_account' }
  | { status: 'not_configured' }
  | { status: 'key_rejected' }
  | { status: 'unreachable'; detail: string };

export async function fetchCampaignPulse(website: string): Promise<CampaignPulse> {
  const { data } = await api.get<CampaignPulse>('/campaign-history/pulse', { params: { website } });
  return data;
}

/** [CP-ROUTES 2026-09-10] Whether ClientPulse's timeline is available for this website.
 *  [CP-FRAME 2026-09-10] Only the outcome — the link and its token stay on our server, which serves
 *  the page itself (see `campaignPulseFrameUrl`). */
export type CampaignPulseEmbed =
  | { status: 'ok' }
  | { status: 'no_account' }
  | { status: 'not_configured' }
  | { status: 'key_rejected' }
  | { status: 'unreachable'; detail: string };

export async function fetchCampaignPulseEmbed(website: string): Promise<CampaignPulseEmbed> {
  const { data } = await api.get<CampaignPulseEmbed>('/campaign-history/pulse/embed', { params: { website } });
  return data;
}

/** [CP-FRAME 2026-09-10, user] The address of ClientPulse's timeline page as OUR server serves it —
 *  their page with the app's scrollbar style — for an <iframe>. `theme` is the page's own colours
 *  (preset, bg, card, text, muted, line, accent, radius, shadow); the server re-validates each one.
 *  It is a plain navigation, not an XHR, so it goes to the API directly: in development CRA's proxy
 *  never forwards a text/html request, which is the same reason AUTH_BASE exists. */
export function campaignPulseFrameUrl(website: string, theme: Record<string, string>): string {
  const qs = new URLSearchParams({ website, ...theme });
  return `${AUTH_BASE}/api/campaign-history/pulse/frame?${qs.toString()}`;
}

/* [ORDER-CONFIRMATION 2026-09-21, user] Campaign History → "Order Confirmation" tab: the other
   team's order confirmations for the website (every field they send, flattened on our server) and
   the payments we received on it, with the month list and the month to open on. */
export interface CampaignOrderField { label: string; value: string; kind: 'text' | 'date' | 'money' | 'link' }
export interface CampaignOrderRecord {
  month: string | null; date: string | null; fields: CampaignOrderField[];
  /** [OC-TEMPLATE 2026-09-21] The finished OC document in the organisation's template (https PDF). */
  pdfUrl: string | null;
}
export interface CampaignOrderPayment {
  month: string; date: string; amount: number;
  account: string | null; invoice: string | null; description: string | null;
  refunded: number; disputed: number;
  /* [OC-CARD 2026-09-21] One order-confirmation card per payment: who paid, what it was booked as,
     and the Order Confirmation system's record(s) for it. */
  client: string | null; email: string | null; pm: string | null; paymentId: string | null;
  kinds: string[];
  oc: CampaignOrderRecord[];
}
export interface CampaignOrder {
  /** The API's outcome only — our payments are in the answer whatever this says. */
  status: 'ok' | 'not_configured' | 'key_rejected' | 'unreachable';
  records: CampaignOrderRecord[];
  /** [OC-CARD] Records not tied to any payment. */
  unmatched: CampaignOrderRecord[];
  /** [OC-ONLY 2026-09-21] Only payments the Order Confirmation system has a record for. */
  payments: CampaignOrderPayment[];
  /** [OC-ONLY] Only months holding an order confirmation; empty = no OC for this website. */
  months: string[];
  defaultMonth: string;
  currentMonth: string;
}
export async function fetchCampaignOrder(website: string): Promise<CampaignOrder> {
  const { data } = await api.get<CampaignOrder>('/campaign-history/order', { params: { website } });
  return data;
}
/** [OC-PAGES 2026-09-21] One OC document's bytes, passed through our server (their S3 sends no CORS
 *  headers, so the page cannot read it directly). `url` must be a record's `pdfUrl`. */
export async function fetchCampaignOrderPdf(website: string, url: string): Promise<ArrayBuffer> {
  const { data } = await api.get<ArrayBuffer>('/campaign-history/order/pdf', {
    params: { website, url },
    responseType: 'arraybuffer',
  });
  return data;
}
